import Button from 'tio-reimagined/components/button';
import { Listbox } from 'tio-ui/components/collections';
import Drawer from 'tio-reimagined/components/drawer';
import Popover from 'tio-reimagined/components/popover';
import EmberFlatpickr from 'ember-flatpickr/components/ember-flatpickr';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { Section, VStack } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import pageTitle from 'ember-page-title/helpers/page-title';
import type IntlService from 'ember-intl/services/intl';
import type FeaturesService from '../../../services/features.ts';
import type TasApplicationModel from '../../../models/tas-application.ts';
import type TasProgramInstanceModel from '../../../models/tas-program-instance.ts';
import {
  // PhSparkle,
  // PhThumbsUp,
  // PhThumbsDown,
  PhPencil,
  PhDotsThreeVertical,
  PhArrowRight,
  PhX,
  PhPlus,
  PhCalendarBlank,
} from 'ember-phosphor-icons';
import type { AppHistoryBase } from '../../../types/tuition-assistance.ts';
import type { ItemSignature } from '../../tas/feed/feed-container.ts';
import type { CustomFieldSignature, CustomFieldOption } from '../../../types/tuition-assistance.ts';
import { formatAsDollars, setSemesterCodeString } from '../../../utils/format.ts';
import { formatDateToISO, formatISODateToDisplay } from '../../../utils/date/index.ts';
import { trackedObject } from '@ember/reactive/collections';
import { dropTask } from 'ember-concurrency';
import type Store from '@ember-data/store';
import type TasCourseModel from '../../../models/tas-course.ts';
import type TuitionAssistanceService from '../../../services/tuition-assistance.ts';
import type Owner from '@ember/owner';
import {
  getSingleValueForTasField,
  setSingleValueForTasField,
  getSortedOptionsByFieldName,
} from '../../../utils/tuition-assistance/fields.ts';
import { checkEmployeeRepaymentStatus } from '../../../utils/tas-application-repayment-check.ts';
import * as v from 'valibot';
import type { ValidationErrorSignature } from '../../tuition-assistance/forms/courses/grade-select-options.ts';
import Input from 'tio-reimagined/components/input';
import Textarea from 'tio-reimagined/components/textarea';
import ReSelect from 'tio-reimagined/components/select';
import { eq, or, not } from 'tio-ui/utilities';
import inputmask from '../../../modifiers/inputmask.ts';
import TuitionAssistanceProgramFormField from '../../tuition-assistance/program-form-field.gts';
import TuitionAssistanceFormsCustomFieldsForProvince from '../../tuition-assistance/forms/custom-fields-for-province.gts';
import SchoolSelect from '../../tuition-assistance/program-details/school-select.gts';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import type TasUnifiedInstitutionModel from '../../../models/tas-unified-institution.ts';
import type { TasUnifiedInstitution } from '../../../models/search.ts';
import type {
  LimitsStatusDataSignature,
  EligibilityStatusSignature,
} from '../../../services/tuition-assistance.ts';
import type RouterService from '@ember/routing/router-service';
import {
  validateBeginDate,
  validateEndDate,
  type FieldConfig,
} from '../../../utils/tuition-assistance/course-date-validation.ts';
import errorsForValibotField from '../../../helpers/errors-for-valibot-field.ts';

// Type definitions for AI recommendation response
type AiRecommendationResponse = {
  recommendation_summary?: {
    status?: string;
    calculation_details?: unknown;
  };
};

// Type definition for program template fields
// Used throughout the component to type-cast programTemplate.fields
type ProgramTemplateFields =
  | Record<string, { visible?: boolean; label?: string; required?: boolean }>
  | undefined;

// Type definition for expense row objects
type ExpenseRow = {
  label: string;
  amount: string;
  isTotal: boolean;
  type: 'course' | 'scholarship' | 'course-cash' | 'total';
  courseIndex?: number;
};

// Import extracted components from tio-common
import ApprovalFooter from './approval-footer.gts';
import AiAnalysisDrawer from './drawers/ai-analysis.gts';
import CommentsDrawer from './drawers/comments.gts';
import CoursesDrawer from './drawers/courses.gts';
import PartnerInfoDrawer from './drawers/partner-info.gts';
import FinancialDetailsDrawer from './drawers/financial-details.gts';
import AttachmentsDrawer from './drawers/attachments.gts';
import AttachmentsAnalysisView from './drawers/ai-analysis/attachments-analysis-view.gts';
import TimelineDrawer from './drawers/timeline.gts';
import ApprovalQueueStatusDrawer from './drawers/approval-queue-status.gts';
import FinancialDetailsAdminSummary from './financial-details-admin-summary.gts';
import ApprovalDecisionBanner from './approval-decision-banner.gts';
import AddScholarshipsDrawer from '../add-scholarships-drawer.gts';
import RecentCommentPreview from './recent-comment-preview.gts';
import ExpandableText from '../../expandable-text.gts';

const classificationOptions = [
  { label: 'Final Approval Document', key: 'COMPLETION_DOCUMENTS' },
  { label: 'Approval Document', key: 'COURSES_APPROVAL_DOCUMENTS' },
  { label: 'Misc Approval Document (Admin)', key: 'APPLICATION_LIFECYCLE_DOCUMENTS' },
];

/**
 * Configuration for editable program instance fields
 *
 * NOTE: SCHOOL_INSTITUTION_NAME uses SchoolSelect component (not simple text input)
 */
const PROGRAM_INSTANCE_FIELD_CONFIGS = [
  { name: 'SCHOOL_INSTITUTION_NAME', type: 'school-select', testId: 'school-name' },
  { name: 'PROGRAM_SPECIALIZATION', type: 'text', testId: 'specialization' },
  { name: 'PROGRAM_MAJOR', type: 'text', testId: 'major' },
  { name: 'ESTIMATED_PROGRAM_BEGIN', type: 'date', testId: 'program-begin' },
  { name: 'ESTIMATED_PROGRAM_COMPLETION', type: 'date', testId: 'program-completion' },
  { name: 'ESTIMATED_COST', type: 'currency', testId: 'estimated-cost' },
  { name: 'ENROLLMENT_OBJECTIVE', type: 'text', testId: 'enrollment-objective' },
] as const;

/**
 * Configuration for editable application fields
 */
const APPLICATION_FIELD_CONFIGS = [
  { name: 'COURSES_BEGIN_DATE', type: 'date', testId: 'courses-begin', hasValidation: true },
  { name: 'COURSES_END_DATE', type: 'date', testId: 'courses-end', hasValidation: true },
  { name: 'COURSES_RECEIPT_DATE', type: 'date', testId: 'courses-receipt' },
  { name: 'SEMESTER_CODE', type: 'text', testId: 'semester-code' },
] as const;

interface ApprovalPageSignature {
  Element: HTMLElement;
  Args: {
    model: TasApplicationModel;
    /** Query param to enable AI analysis in production (tio-admin only) */
    ai?: boolean;
    /** Optional callback to open the CourseCash drawer (admin-only) */
    onCourseCashOpen?: () => void;
  };
  Blocks: {
    header: [];
    alerts?: [];
  };
}

export default class TasApprovalPage extends Component<ApprovalPageSignature> {
  @service declare sessionContext: unknown;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare features: FeaturesService;
  @service declare router: RouterService;

  @tracked selectedDrawerType: string | null = null;
  @tracked isDrawerOpen = false;
  @tracked selectedCourseIndex = 0;
  @tracked showUploadSection = false;
  @tracked selectedClassification = '';
  @tracked uploadClassificationField = '';

  // Expenses popover and scholarship drawer state
  @tracked isExpensesPopoverOpen = false;
  @tracked isScholarshipDrawerOpen = false;

  // Inline course expense editing
  @tracked editingCourseExpensesIndex: number | null = null;
  @tracked courseExpenseFieldsCopy: TasCourseModel['fields'] | null = null;
  @tracked expenseChangelog = '';

  // Inline course detail editing
  @tracked editingCourseDetailsIndex: number | null = null;
  @tracked courseDetailFieldsCopy: TasCourseModel['fields'] | null = null;
  @tracked courseCustomFieldsCopy: CustomFieldSignature[] | null = null;
  @tracked detailChangelog = '';

  // Inline application details editing
  @tracked isEditingApplicationDetails = false;
  @tracked applicationFieldsCopy: TasApplicationModel['fields'] | null = null;
  @tracked applicationCustomFieldsCopy: CustomFieldSignature[] | null = null;
  @tracked programInstanceFieldsCopy: TasProgramInstanceModel['fields'] | null = null;
  @tracked programInstanceCustomFieldsCopy: CustomFieldSignature[] | null = null;
  @tracked applicationDetailsChangelog = '';
  @tracked selectedSchool?: TasUnifiedInstitutionModel;
  @tracked hasSubmittedApplicationDetails = false;
  @tracked courseDateValidationErrors: Record<string, readonly string[]> = {};

  // Financial details state
  @tracked editableApplicationTotal = 0;
  @tracked editableCourseCashTotal = 0;

  // Comments drawer tab state
  @tracked commentsInitialTab: 'approver' | 'employee' = 'approver';

  // V2 Limits data
  @tracked limitsData: LimitsStatusDataSignature | undefined = undefined;

  // Eligibility status data (admin decision-assist banner)
  @tracked eligibilityData: EligibilityStatusSignature | undefined = undefined;
  @tracked eligibilityLoading = false;

  // Repayment check - runs on component load
  repaymentCheckData = trackedFunction(this, async () => {
    const employeeId = this.args.model.tasProgramInstance?.employee?.id;
    const companyId = this.args.model.tasProgramInstance?.employee?.company?.id;
    if (!employeeId || !companyId) {
      return null;
    }
    return await checkEmployeeRepaymentStatus(this.store, employeeId, companyId);
  });

  // Employee eligibility periods - used for checking if employee is eligible today
  periodsOfEligibilityData = trackedFunction(this, async () => {
    try {
      const programInstance = this.args.model.tasProgramInstance;
      if (!programInstance?.employee?.tasParticipant?.id) {
        return [];
      }

      const data = await this.store
        .adapterFor('tas-participant')
        .periodOfEligibility(
          programInstance.employee.tasParticipant.id,
          programInstance.tasProgramTemplate.code
        );
      return data;
    } catch (e) {
      console.warn('Error fetching eligibility periods:', e);
      return [];
    }
  });

  constructor(owner: Owner, args: ApprovalPageSignature['Args']) {
    super(owner, args);

    // Initialize financial state
    const calculateTotalByClaimsFinancials =
      args.model.tasProgramInstance?.tasProgramTemplate?.calculateTotalByClaimsFinancials;

    this.editableApplicationTotal = calculateTotalByClaimsFinancials
      ? args.model.recalculateClaimsFinancialsAfterAdjacentUpdate().CF_REIMBURSEMENT_TOTAL
      : ((getSingleValueForTasField(
          args.model.fieldForEditableApprovedTotal,
          args.model.fields
        ) as number) ??
        (args.model.approvedTotal as number) ??
        (args.model.requestedTotal as number));

    this.editableCourseCashTotal = Number(args.model.cardPreApprovedTotal);

    // Load V2 limits data
    void this.loadLimitsData();

    // Load eligibility status for admin decision banner
    if (this.features.isAdminApp) {
      void this.loadEligibilityData();
    }
  }

  /**
   * Loads V2 limits data for the employee and application
   * This provides scheme-level limits information used to display remaining balance
   */
  loadLimitsData = async () => {
    const employee = this.args.model.tasProgramInstance?.employee;
    if (!employee) {
      console.warn('Cannot load limits data: employee not found on application');
      return;
    }

    try {
      this.limitsData = await this.tuitionAssistance.getV2LimitsStatus(employee, this.args.model);
    } catch (error) {
      console.error('Error loading V2 limits data:', {
        applicationId: this.args.model.id,
        employeeId: employee.id,
        error,
      });
    }
  };

  /**
   * Loads eligibility status data for the admin decision-assist banner.
   * This provides participant state, employment status, and computed eligibility.
   */
  loadEligibilityData = async () => {
    const applicationId = this.args.model.id;
    if (!applicationId) {
      return;
    }

    this.eligibilityLoading = true;
    try {
      this.eligibilityData = await this.tuitionAssistance.getEligibilityStatus(applicationId);
    } catch (error) {
      console.error('Error loading eligibility status:', {
        applicationId,
        error,
      });
    } finally {
      this.eligibilityLoading = false;
    }
  };

  openDrawer = (type: string) => {
    this.selectedDrawerType = type;
    this.isDrawerOpen = true;
  };

  openCommentsDrawer = (tab: 'approver' | 'employee' = 'approver') => {
    this.commentsInitialTab = tab;
    this.openDrawer('comments');
  };

  closeDrawer = () => {
    this.isDrawerOpen = false;
    this.selectedDrawerType = null;

    // Cancel any active editing when closing drawer
    if (this.editingCourseExpensesIndex !== null) {
      this.editingCourseExpensesIndex = null;
      this.courseExpenseFieldsCopy = null;
      this.expenseChangelog = '';
    }

    if (this.editingCourseDetailsIndex !== null) {
      this.editingCourseDetailsIndex = null;
      this.courseDetailFieldsCopy = null;
      this.detailChangelog = '';
    }
  };

  courseTabDidChange = (index: number) => {
    this.selectedCourseIndex = index;
  };

  openCourseDrawer = (courseIndex: number) => {
    this.selectedCourseIndex = courseIndex;
    this.openDrawer('course-details');
  };

  handleExpenseRowClick = (row: ExpenseRow) => {
    if (row.type === 'course' && row.courseIndex !== undefined) {
      this.openCourseDrawer(row.courseIndex);
    } else if (row.type === 'scholarship') {
      this.isScholarshipDrawerOpen = true;
    }
    // course-cash and total rows are not clickable
  };

  toggleUploadSection = () => {
    this.showUploadSection = !this.showUploadSection;
    // Reset classification when opening
    if (this.showUploadSection) {
      this.selectedClassification = '';
      this.uploadClassificationField = '';
    }
  };

  hideUploadSection = () => {
    this.showUploadSection = false;
    this.selectedClassification = '';
    this.uploadClassificationField = '';
  };

  attachmentClassificationDidChange = (classification: string) => {
    this.selectedClassification = classification;
    this.uploadClassificationField = classification || 'APPLICATION_LIFECYCLE_DOCUMENTS';
  };

  get showRepaymentWarning() {
    // Only show warning if OTHER applications are in repayment (exclude current app)
    return this.repaymentApplicationIds.length > 0;
  }

  get repaymentApplicationIds() {
    const allApps = this.repaymentCheckData.value?.applications ?? [];
    const currentAppId = String(this.args.model.id);
    // Filter out the current application from the repayment warning list
    return allApps.filter((app) => String(app.applicationId) !== currentAppId);
  }

  /**
   * Get the correct approval route name based on consuming app
   * tio-admin: authenticated.tas.applications.approval
   * tio-employee: authenticated.admin.tas.applications.approval
   */
  get repaymentApplicationApprovalRoute() {
    return this.consumingApp === 'tio-admin'
      ? 'authenticated.tas.applications.approval'
      : 'authenticated.admin.tas.applications.approval';
  }

  // Expenses popover and scholarship drawer handlers
  openExpensesPopover = () => {
    this.isExpensesPopoverOpen = true;
  };

  closeExpensesPopover = () => {
    this.isExpensesPopoverOpen = false;
  };

  handleExpensesMenu = (key: string) => {
    // Close popover first
    this.isExpensesPopoverOpen = false;

    // Add delay to allow popover to close before opening drawer
    setTimeout(() => {
      if (key === 'addScholarship') {
        this.isScholarshipDrawerOpen = true;
      }
    }, 250);
  };

  closeScholarshipDrawer = () => {
    this.isScholarshipDrawerOpen = false;
    // Sync scholarships from the saved application back to the tracked copy
    this.syncScholarshipsFromApplication();
  };

  syncScholarshipsFromApplication() {
    // If we're in edit mode and have a tracked copy, update it with the latest scholarship data
    if (this.applicationFieldsCopy && this.args.model.fields['SCHOLARSHIPS_RECEIVED']) {
      this.applicationFieldsCopy['SCHOLARSHIPS_RECEIVED'] =
        this.args.model.fields['SCHOLARSHIPS_RECEIVED'];
    }
  }

  toggleCourseExpenseEdit = () => {
    const isCurrentlyEditing = this.editingCourseExpensesIndex === this.selectedCourseIndex;

    if (isCurrentlyEditing) {
      // Cancel edit mode - discard changes
      this.editingCourseExpensesIndex = null;
      this.courseExpenseFieldsCopy = null;
      this.expenseChangelog = '';
    } else {
      // Enter edit mode - create tracked copy
      const course = this.selectedCourse;
      if (!course) return;

      this.courseExpenseFieldsCopy = trackedObject(course.fields);
      this.expenseChangelog = '';
      this.editingCourseExpensesIndex = this.selectedCourseIndex;
    }
  };

  updateCourseExpenseField = (fieldName: string, value: string, event?: Event) => {
    if (!this.courseExpenseFieldsCopy) return;

    let valueToSet: number;
    const target = event?.target as HTMLInputElement | undefined;

    // @ts-expect-error - inputmask types not fully defined
    if (target?.inputmask?.userOptions?.alias === 'currency') {
      // Get unmasked value from inputmask and convert to cents
      valueToSet = Number(target.inputmask?.unmaskedvalue()) * 100;
    } else {
      // Fallback: parse string value and convert to cents
      valueToSet = value ? parseFloat(value.replace(/[^0-9.-]/g, '')) * 100 : 0;
    }

    setSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      valueToSet,
      this.courseExpenseFieldsCopy
    );
  };

  updateExpenseChangelog = (value: string) => {
    this.expenseChangelog = value;
  };

  saveCourseExpenses = dropTask(async () => {
    const course = this.selectedCourse;
    if (!course || !this.courseExpenseFieldsCopy) {
      return;
    }

    // Validate changelog before saving
    if (!this.isExpenseFormValid) {
      return;
    }

    try {
      // Update course with edited expense fields
      course.fields = this.courseExpenseFieldsCopy;

      // Save course with changelog (passed via adapterOptions)
      await course.save({ adapterOptions: { changelog: this.expenseChangelog } });

      const application = this.args.model;

      // Handle direct pay program totals recalculation if needed
      if (
        course.tasApplication?.tasProgramInstance?.tasProgramTemplate?.isDirectPayProgram &&
        course.tasApplication.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH'
      ) {
        this.setSubtotalsForDirectPay();
        // Application was modified by setSubtotalsForDirectPay, save it
        await application.save({ adapterOptions: { changelog: this.expenseChangelog } });
      }

      // Recalculate claims financials if needed
      if (application.tasProgramInstance?.tasProgramTemplate?.calculateTotalByClaimsFinancials) {
        const calculatorResults = application.recalculateClaimsFinancialsAfterAdjacentUpdate();

        if (application.approvedTotal !== calculatorResults.CF_REIMBURSEMENT_TOTAL) {
          await this.updateApprovedTotal(calculatorResults.CF_REIMBURSEMENT_TOTAL);
        }
      }

      // Refresh limits
      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();

      // Exit edit mode and clear state
      this.editingCourseExpensesIndex = null;
      this.courseExpenseFieldsCopy = null;
      this.expenseChangelog = '';

      // Refresh course expenses display
      this.courseExpensesFor(course);
    } catch (error) {
      console.error('Error saving course expenses:', error);
      // TODO: Show error notification to user
    }
  });

  updateApprovedTotal = async (
    approvedTotal: number,
    reason: string = 'Course expenses updated',
    additionalFieldsToUpdate?: Record<string, unknown>
  ) => {
    const additionalFieldValues: Record<string, unknown> = {
      ...additionalFieldsToUpdate,
    };

    const calculateTotalByClaimsFinancials =
      this.args.model.tasProgramInstance?.tasProgramTemplate?.calculateTotalByClaimsFinancials;

    if (calculateTotalByClaimsFinancials) {
      const claims = this.args.model.recalculateClaimsFinancialsAfterAdjacentUpdate();
      additionalFieldValues['CLAIMS_FINANCIALS'] = claims;
    }

    // Don't save the course cash total separately - let updateApprovedTotal handle it
    // This ensures the approved_total_log captures the correct "from" value

    const updatedApplication = await this.store
      .adapterFor('tas-application')
      .updateApprovedTotal(this.args.model, approvedTotal, reason, additionalFieldValues);

    const beforePreApprovalStates = [
      'TAS.ApplicationState.CREATED',
      'TAS.ApplicationState.DEFINE_COURSES',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ];

    const isBeforePreApprovalState = beforePreApprovalStates.includes(this.args.model.state);

    const fieldToCheck = isBeforePreApprovalState ? 'PRE_APPROVED_TOTAL' : 'FINAL_APPROVED_TOTAL';
    const actualUpdatedValue = getSingleValueForTasField(
      fieldToCheck,
      updatedApplication.fields
    ) as number;

    if (actualUpdatedValue !== approvedTotal) {
      throw new Error(
        `Unable to update approved total. expected=${approvedTotal} actual=${actualUpdatedValue} (checked field: ${fieldToCheck})`
      );
    }

    this.editableApplicationTotal = updatedApplication.approvedTotal as number;
    this.editableCourseCashTotal = updatedApplication.courseCashTotal as number;
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  // Financial details methods
  setApplicationTotal = (value: number) => {
    this.editableApplicationTotal = Math.round(value * 100);
  };

  setCourseCashTotal = (value: number) => {
    this.editableCourseCashTotal = Math.round(value * 100);
  };

  setCourseCashApprovedTotal = async (amount: number) => {
    const paymentMethodIsCourseCash =
      this.args.model.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
    if (!paymentMethodIsCourseCash && !amount) return;

    setSingleValueForTasField('CARD_FINAL_APPROVED_TOTAL', amount, this.args.model.fields);
    await this.args.model.save();
  };

  didUpdateClaimsFinancials = (claimsFinancials: { CF_REIMBURSEMENT_TOTAL: number }) => {
    setSingleValueForTasField('CLAIMS_FINANCIALS', claimsFinancials, this.args.model.fields);
    this.editableApplicationTotal = claimsFinancials.CF_REIMBURSEMENT_TOTAL;
  };

  saveUpdatedClaimsFinancialsWithApprovedTotal = async (
    claimsFinancials: { CF_REIMBURSEMENT_TOTAL: number },
    reason: string
  ) => {
    this.editableApplicationTotal = claimsFinancials.CF_REIMBURSEMENT_TOTAL;
    this.didUpdateClaimsFinancials(claimsFinancials);
    await this.updateApprovedTotal(this.editableApplicationTotal, reason);
  };

  updateInvoiceDate = (date: string) => {
    setSingleValueForTasField('INVOICE_DATE', date, this.args.model.fields);
  };

  setSubtotalsForDirectPay() {
    const { requestedCourseCashSubtotalLessScholarships, requestedNonCourseCashSubtotal } =
      this.args.model;

    const typeClassification = this.args.model.typeClassification || '';
    const isTypeTwoProgram = typeClassification === 'TAS.ProgramType.2';

    const courseCashTotalField = isTypeTwoProgram
      ? 'CARD_FINAL_APPROVED_TOTAL'
      : 'CARD_PRE_APPROVED_TOTAL';
    const nonCourseCashTotalField = isTypeTwoProgram
      ? 'FINAL_APPROVED_TOTAL'
      : 'PRE_APPROVED_TOTAL';

    setSingleValueForTasField(
      courseCashTotalField,
      requestedCourseCashSubtotalLessScholarships,
      this.args.model.fields
    );
    setSingleValueForTasField(
      nonCourseCashTotalField,
      requestedNonCourseCashSubtotal,
      this.args.model.fields
    );
  }

  get isAiAnalysis() {
    return this.selectedDrawerType === 'ai-analysis';
  }

  get isComments() {
    return this.selectedDrawerType === 'comments';
  }

  get isEmployerInfo() {
    return this.selectedDrawerType === 'employer-info';
  }

  get isFinancialDetails() {
    return this.selectedDrawerType === 'financial-details';
  }

  get isAttachments() {
    return this.selectedDrawerType === 'attachments';
  }

  get isTimeline() {
    return this.selectedDrawerType === 'timeline';
  }

  get isCourseDetails() {
    return this.selectedDrawerType === 'course-details';
  }

  get isReassignApprover() {
    return this.selectedDrawerType === 'reassign-approver';
  }

  get consumingApp(): string {
    return this.features.isAdminApp
      ? 'tio-admin'
      : this.features.isEmployeeApp
        ? 'tio-employee'
        : this.features.isAgentApp
          ? 'tio-agent'
          : 'unknown';
  }

  /**
   * Get the correct route for program template based on the consuming app
   * tio-admin: authenticated.tuition-assistance.program-builder.program-template
   * tio-employee: authenticated.admin.tuition-assistance.program-templates.show
   */
  get programTemplateRoute(): string | undefined {
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!programTemplate?.id) return undefined;

    return this.consumingApp === 'tio-admin'
      ? 'authenticated.tuition-assistance.program-builder.program-template'
      : 'authenticated.admin.tuition-assistance.program-templates.show';
  }

  get selectedCourse() {
    const courses = this.args.model.activeCourses || [];
    return courses[this.selectedCourseIndex];
  }

  get currentDrawerComponent() {
    return null; // We'll use conditional rendering instead
  }

  get approverInstructionsItems() {
    const instructions =
      this.args.model.tasProgramInstance?.tasProgramTemplate?.programApproverInstructions || [];
    return instructions.map((instruction) => ({
      heading: instruction.heading || `Instruction ${instruction.ordinal}`,
      body: instruction.body,
      ordinal: instruction.ordinal,
    }));
  }

  get employeeFaqsItems() {
    const faqs = this.args.model.tasProgramInstance?.tasProgramTemplate?.programFaqs || [];
    return faqs.map((faq) => ({
      heading: faq.heading || faq.question || 'FAQ',
      body: faq.body || faq.answer || '',
      ordinal: faq.ordinal || 0,
    }));
  }

  get approvalStatus() {
    // Map application state to human-readable status
    const state = this.args.model.state;
    if (state?.includes('PENDING_COURSES_APPROVAL')) {
      return 'Pending Courses Approval';
    } else if (state?.includes('PENDING_EVIDENCE_APPROVAL')) {
      return 'Pending Evidence Approval';
    } else if (state?.includes('APPROVED')) {
      return 'Approved';
    } else if (state?.includes('REJECTED')) {
      return 'Rejected';
    }
    return 'Pending Review';
  }

  get applicationNumber() {
    return this.args.model.id || 'Unknown';
  }

  get employeeName() {
    const employee = this.args.model.tasProgramInstance?.employee;
    const person = employee?.person;
    if (person?.firstName && person?.lastName) {
      return `${person.firstName} ${person.lastName}`;
    }
    return person?.fullName || 'Unknown';
  }

  get payrollId() {
    return this.args.model.tasProgramInstance?.employee?.payrollId || 'Unknown';
  }

  get remainingBalance() {
    // Use V2 limits data - scheme.available is the correct limit to display
    const schemeAvailable = this.limitsData?.details?.scheme?.available;

    if (schemeAvailable !== undefined && schemeAvailable !== null) {
      return `$${(schemeAvailable / 100).toFixed(2)}`;
    }

    // Fallback to courseCashAvailableBalance if V2 data not yet loaded
    const balance = this.args.model.courseCashAvailableBalance;
    return balance !== undefined ? `$${(balance / 100).toFixed(2)}` : '$0.00';
  }

  get requestedAmount() {
    if (this.args.model.isCreditBased) {
      const credits = this.args.model.totalCredits || 0;
      return `${credits} credits`;
    }
    const amount = this.args.model.requestedTotal || 0;
    return amount ? `$${(amount / 100).toFixed(2)}` : '$0.00';
  }

  get isParticipantInactive() {
    return !this.args.model.tasProgramInstance?.employee?.tasParticipant?.isActive;
  }

  get annualLimit() {
    // Use V2 limits data - scheme.annual_limit is the correct annual limit
    const annualLimit = this.limitsData?.details?.scheme?.annual_limit;

    if (annualLimit !== undefined && annualLimit !== null) {
      return `$${(annualLimit / 100).toFixed(2)}`;
    }

    return 'N/A';
  }

  get courseCount() {
    return this.args.model.activeCourses?.length || 0;
  }

  get useBalanceMultiplier() {
    return (
      this.args.model.tasProgramInstance?.tasProgramTemplate
        ?.useBalanceMultiplierForProgramLimits || false
    );
  }

  get allAttachments() {
    // Assets can be attached to the program instance, course, application, or assistance
    const programInstanceAssets = this.args.model.tasProgramInstance?.tasAssets || [];
    const courseAssets =
      this.args.model.activeCourses?.flatMap((course) => course.tasAssets || []) || [];
    const appAssets = this.args.model.tasAssets || [];
    const assistanceAssets =
      this.args.model.tasAssistances?.flatMap((assistance) => assistance.tasAssets || []) || [];

    // Deduplicate by ID since assets may be linked to multiple relationships
    const seenIds = new Set<string>();
    const allAssets = [
      ...programInstanceAssets,
      ...courseAssets,
      ...appAssets,
      ...assistanceAssets,
    ];
    return allAssets.filter((asset) => {
      const id = asset.id;
      if (!id || seenIds.has(id)) return false;
      seenIds.add(id);
      return true;
    });
  }

  get allAssetSummaries() {
    // Get asset summaries from all attachments
    // Filter out null/undefined values with proper type guard
    const summaries = this.allAttachments
      .map((asset) => asset.assetSummary)
      .filter((summary): summary is NonNullable<typeof summary> => summary != null);

    return summaries;
  }

  get timelineHistory() {
    // Use fullHistory which combines application_history and payments_history
    return this.args.model.fullHistory || [];
  }

  get hasTimelineData() {
    return this.timelineHistory.length > 0;
  }

  get aiRecommendation() {
    const recommendations = this.args.model.aiRecommendations;
    if (!recommendations || recommendations.length === 0) return undefined;
    // Use the most recent recommendation
    const sorted = [...recommendations].sort(
      (a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
    );
    return sorted[0];
  }

  get aiRecommendationStatus() {
    const responseJson = this.aiRecommendation?.responseJson as
      | AiRecommendationResponse
      | undefined;
    const status = responseJson?.recommendation_summary?.status;
    if (!status) return null;

    // Map status to human-readable text
    const statusMap: Record<string, string> = {
      approved_for_requested_amount: 'Approve Application',
      approved_for_less_than_requested: 'Approve (Less Than Requested)',
      approved_for_more_than_requested: 'Approve (More Than Requested)',
      denied: 'Do Not Approve',
    };

    return statusMap[status] || status;
  }

  get aiCalculationDetails() {
    const responseJson = this.aiRecommendation?.responseJson as
      | AiRecommendationResponse
      | undefined;
    const details = responseJson?.recommendation_summary?.calculation_details;
    // If details is an object, stringify it for display, otherwise return as string
    if (details && typeof details === 'object') {
      return JSON.stringify(details, null, 2);
    }
    return details ? String(details) : null;
  }

  get hasAiRecommendation() {
    return !!this.aiRecommendation && !!this.aiRecommendationStatus;
  }

  /**
   * Get custom fields for EMPLOYEE province from program instance
   * In edit mode, uses tracked copy; otherwise uses live model data
   * Sorted by ordinal for consistent display
   */
  get customEmployeeFields(): CustomFieldSignature[] {
    let fields: CustomFieldSignature[] = [];

    if (this.isEditingApplicationDetails && this.programInstanceCustomFieldsCopy) {
      fields = this.programInstanceCustomFieldsCopy.filter(
        (field) => field.province === 'EMPLOYEE'
      );
    } else {
      const programInstance = this.args.model.tasProgramInstance;
      fields = (programInstance?.customFields || []).filter(
        (field) => field.province === 'EMPLOYEE'
      );
    }

    return fields.sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
  }

  /**
   * Get custom fields for PROGRAM province from program instance
   * In edit mode, uses tracked copy; otherwise uses live model data
   * Sorted by ordinal for consistent display
   */
  get customProgramFields(): CustomFieldSignature[] {
    let fields: CustomFieldSignature[] = [];

    if (this.isEditingApplicationDetails && this.programInstanceCustomFieldsCopy) {
      fields = this.programInstanceCustomFieldsCopy.filter((field) => field.province === 'PROGRAM');
    } else {
      const programInstance = this.args.model.tasProgramInstance;
      fields = (programInstance?.customFields || []).filter(
        (field) => field.province === 'PROGRAM'
      );
    }

    return fields.sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
  }

  /**
   * Get custom fields for APPLICATION province from application
   * In edit mode, uses tracked copy; otherwise uses live model data
   * Sorted by ordinal for consistent display
   */
  get customApplicationFields(): CustomFieldSignature[] {
    let fields: CustomFieldSignature[] = [];

    if (this.isEditingApplicationDetails && this.applicationCustomFieldsCopy) {
      fields = this.applicationCustomFieldsCopy.filter((field) => field.province === 'APPLICATION');
    } else {
      const application = this.args.model;
      fields = (application?.customFields || []).filter(
        (field) => field.province === 'APPLICATION'
      );
    }

    return fields.sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
  }

  get formattedHistory(): ItemSignature[] {
    return this.timelineHistory
      .map((item: AppHistoryBase) => {
        let icon;
        let details;
        const formattedName = item.approverName
          ? ' by ' + item.approverName + (item.role ? ` - ${item.role}` : '')
          : '';
        const baseTranslationString = 'tuition_assistance.applications.history';

        switch (item.logType) {
          case 'assistance': {
            icon = 'CurrencyDollar';
            details = this.intl.t(`${baseTranslationString}.${item.assistanceType}`, {
              amount: formatAsDollars(Number(item.amount) || 0),
              name: item.approverName ? ' by ' + item.approverName : '',
            });
            break;
          }
          case 'approval': {
            icon = 'Document';
            const updateType =
              item.from === item.to ? 'approved_total_set' : 'approved_total_updated';
            details = this.intl.t(`${baseTranslationString}.${updateType}`, {
              amount: formatAsDollars(Number(item.to) || 0),
              from: formatAsDollars(Number(item.from) || 0),
              to: formatAsDollars(Number(item.to) || 0),
              name: item.approverName ? ' by ' + item.approverName : '',
            });
            break;
          }
          case 'state_transition': {
            icon = 'UserIcon';
            details = this.intl.t(`${baseTranslationString}.${item.stateTransitionType}`, {
              name: formattedName,
            });

            if (
              item?.stateTransitionType?.includes('NOT_APPROVED') ||
              item?.stateTransitionType?.includes('REJECTED')
            ) {
              icon = 'HandThumbDownIcon';
            } else if (item?.stateTransitionType?.includes('APPROVED')) {
              icon = 'HandThumbUpIcon';
            } else if (
              item.stateTransitionType &&
              ['COMPLETED', 'PRIMED'].includes(item.stateTransitionType)
            ) {
              icon = 'CheckIcon';
            }

            break;
          }
        }

        return {
          ...item,
          details,
          icon,
        } as ItemSignature;
      })
      .sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB.getTime() - dateA.getTime();
      });
  }

  /**
   * Unified field lookup that searches fields and customFields in priority order
   * @param fieldName - The name of the field to retrieve
   * @returns The field value or 'N/A' if not found
   */
  private getFieldValueFromAnyTasField(fieldName: string): string | number | boolean | null {
    // Helper to check if a value is valid (not undefined, null, or empty string)
    const isValidValue = (value: unknown): value is string | number | boolean => {
      return value !== undefined && value !== null && value !== '';
    };

    // Try application fields first
    const appFields = this.args.model.fields as Record<string, { values: unknown[] }> | undefined;
    const appFieldValue = appFields?.[fieldName]?.values?.[0];
    if (isValidValue(appFieldValue)) {
      return appFieldValue;
    }

    // Try application custom fields
    const appCustomField = this.args.model.customFields?.find((f) => f.name === fieldName);
    const appCustomFieldValue = appCustomField?.values?.[0];
    if (isValidValue(appCustomFieldValue)) {
      return appCustomFieldValue;
    }

    // Try program instance fields
    const programInstance = this.args.model.tasProgramInstance;
    const piFields = programInstance?.fields as Record<string, { values: unknown[] }> | undefined;
    const piFieldValue = piFields?.[fieldName]?.values?.[0];
    if (isValidValue(piFieldValue)) {
      return piFieldValue;
    }

    // Try program instance custom fields
    const piCustomField = programInstance?.customFields?.find((f) => f.name === fieldName);
    const piCustomFieldValue = piCustomField?.values?.[0];
    if (isValidValue(piCustomFieldValue)) {
      return piCustomFieldValue;
    }

    return 'N/A';
  }

  /**
   * Merge updated custom fields into existing fields array
   * Matches fields by label and province, then replaces with updated values
   * Pattern adopted from type-two-or-three.gts for consistency
   *
   * @param existingFields - The current array of custom fields
   * @param updatedFields - The array of fields with updated values
   * @returns Merged array with updates applied
   */
  updateFieldsWithNewValues = (
    existingFields: CustomFieldSignature[],
    updatedFields: CustomFieldSignature[]
  ): CustomFieldSignature[] => {
    if (!existingFields) {
      return updatedFields;
    }

    updatedFields.forEach((newObject) => {
      const index = existingFields.findIndex(
        (item) => item.label === newObject.label && item.province === newObject.province
      );
      if (index >= 0) {
        existingFields.splice(index, 1, newObject);
      }
    });

    return existingFields;
  };

  get applicationDetailsRows() {
    const employee = this.args.model.tasProgramInstance?.employee;
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    const templateFields = programTemplate?.fields as ProgramTemplateFields;

    // Helper function to format date - uses UTC to avoid timezone shifting
    const formatDate = (dateStr: string) => {
      return formatISODateToDisplay(dateStr, this.intl, '2-digit');
    };

    // Helper to check if a field is visible
    const isFieldVisible = (fieldName: string): boolean => {
      return templateFields?.[fieldName]?.visible === true;
    };

    // Helper to get field label or use fallback
    const getFieldLabel = (fieldName: string, fallback: string): string => {
      return templateFields?.[fieldName]?.label || fallback;
    };

    const rows = [
      {
        label: 'Employment Type',
        value:
          employee?.employmentType
            ?.replace('_', ' ')
            ?.toLowerCase()
            ?.replace(/\b\w/g, (l) => l.toUpperCase()) || 'N/A',
        isSystemField: true, // Always show system fields
      },
      {
        label: 'Program',
        value: programTemplate?.programName
          ? `${programTemplate.programName} (${programTemplate.code})`
          : 'N/A',
        isSystemField: true,
        linkRoute: this.programTemplateRoute,
        linkModel: programTemplate?.id || undefined,
      },
      {
        label: getFieldLabel('COURSES_BEGIN_DATE', 'Course Starts'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('COURSES_BEGIN_DATE')?.toString() || 'N/A'
        ),
        fieldName: 'COURSES_BEGIN_DATE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('COURSES_END_DATE', 'Course Ends'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('COURSES_END_DATE')?.toString() || 'N/A'
        ),
        fieldName: 'COURSES_END_DATE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('COURSES_RECEIPT_DATE', 'Course Receipt Date'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('COURSES_RECEIPT_DATE')?.toString() || 'N/A'
        ),
        fieldName: 'COURSES_RECEIPT_DATE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('SEMESTER_CODE', 'Semester Code'),
        value: this.getFieldValueFromAnyTasField('SEMESTER_CODE') || 'N/A',
        fieldName: 'SEMESTER_CODE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('SCHOOL_INSTITUTION_NAME', 'Institution'),
        value: this.getFieldValueFromAnyTasField('SCHOOL_INSTITUTION_NAME'),
        fieldName: 'SCHOOL_INSTITUTION_NAME',
        isSystemField: false,
      },
      {
        label: getFieldLabel('PROGRAM_SPECIALIZATION', 'Specialization'),
        value: this.getFieldValueFromAnyTasField('PROGRAM_SPECIALIZATION'),
        fieldName: 'PROGRAM_SPECIALIZATION',
        isSystemField: false,
      },
      {
        label: getFieldLabel('PROGRAM_MAJOR', 'Major'),
        value: this.getFieldValueFromAnyTasField('PROGRAM_MAJOR'),
        fieldName: 'PROGRAM_MAJOR',
        isSystemField: false,
      },
      {
        label: getFieldLabel('ESTIMATED_PROGRAM_BEGIN', 'Program Begin Date'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('ESTIMATED_PROGRAM_BEGIN')?.toString() || 'N/A'
        ),
        fieldName: 'ESTIMATED_PROGRAM_BEGIN',
        isSystemField: false,
      },
      {
        label: getFieldLabel('ESTIMATED_PROGRAM_COMPLETION', 'Program Completion Date'),
        value: formatDate(
          this.getFieldValueFromAnyTasField('ESTIMATED_PROGRAM_COMPLETION')?.toString() || 'N/A'
        ),
        fieldName: 'ESTIMATED_PROGRAM_COMPLETION',
        isSystemField: false,
      },
      {
        label: getFieldLabel('ESTIMATED_COST', 'Estimated Cost'),
        value: (() => {
          const cost = this.getFieldValueFromAnyTasField('ESTIMATED_COST');
          if (!cost || cost === 'N/A') return 'N/A';
          const numericCost = Number(cost);
          return isNaN(numericCost) ? 'N/A' : `$${(numericCost / 100).toFixed(2)}`;
        })(),
        fieldName: 'ESTIMATED_COST',
        isSystemField: false,
      },
      {
        label: getFieldLabel('ENROLLMENT_OBJECTIVE', 'Enrollment Objective'),
        value: this.getFieldValueFromAnyTasField('ENROLLMENT_OBJECTIVE') || 'N/A',
        fieldName: 'ENROLLMENT_OBJECTIVE',
        isSystemField: false,
      },
      {
        label: getFieldLabel('INVOICE_DATE', 'Invoice Date'),
        value: formatDate(this.getFieldValueFromAnyTasField('INVOICE_DATE')?.toString() || 'N/A'),
        fieldName: 'INVOICE_DATE',
        isSystemField: false,
      },
    ];

    // Filter to only show system fields and visible template fields
    return rows.filter(
      (row) => row.isSystemField || (row.fieldName && isFieldVisible(row.fieldName))
    );
  }

  /**
   * Helper function to format custom field values for read-only display
   * Handles currency, date, and option-based formatting
   */
  private formatCustomFieldValue = (
    value: string | boolean | number,
    field: CustomFieldSignature
  ): string => {
    // First check if there's an option mapping (for select/radio/checkbox fields)
    const option = field.options?.find((opt: CustomFieldOption) => opt.value === value);
    if (option) {
      return option.label;
    }

    // Format based on inputType
    const inputType = field.inputType;

    if (inputType === 'currency') {
      // Value is in cents, convert to dollars
      const numValue = Number(value);
      if (isNaN(numValue)) return String(value);
      return `$${(numValue / 100).toFixed(2)}`;
    }

    if (inputType === 'date') {
      // Value is in format "YYYY-MM-DD", convert to "MM/DD/YYYY" using UTC
      return formatISODateToDisplay(String(value), this.intl, 'numeric');
    }

    // Default: return as string
    return String(value);
  };

  /**
   * Get formatted EMPLOYEE province custom field rows for read-only display
   */
  get customEmployeeFieldRows() {
    return this.customEmployeeFields
      .filter((field: CustomFieldSignature) => field.visible)
      .map((field: CustomFieldSignature) => {
        const values = field.values || [];
        if (values.length === 0) return null;

        // Format value(s)
        let formattedValue = '';
        if (values.length > 1) {
          formattedValue = values
            .map((value) => this.formatCustomFieldValue(value, field))
            .join(', ');
        } else if (values[0] !== undefined) {
          formattedValue = this.formatCustomFieldValue(values[0], field);
        }

        return {
          label: field.label || field.name || '',
          value: formattedValue,
        };
      })
      .filter((row: { label: string; value: string } | null) => row !== null);
  }

  /**
   * Get formatted PROGRAM province custom field rows for read-only display
   */
  get customProgramFieldRows() {
    return this.customProgramFields
      .filter((field: CustomFieldSignature) => field.visible)
      .map((field: CustomFieldSignature) => {
        const values = field.values || [];
        if (values.length === 0) return null;

        // Format value(s)
        let formattedValue = '';
        if (values.length > 1) {
          formattedValue = values
            .map((value) => this.formatCustomFieldValue(value, field))
            .join(', ');
        } else if (values[0] !== undefined) {
          formattedValue = this.formatCustomFieldValue(values[0], field);
        }

        return {
          label: field.label || field.name || '',
          value: formattedValue,
        };
      })
      .filter((row: { label: string; value: string } | null) => row !== null);
  }

  /**
   * Get formatted APPLICATION province custom field rows for read-only display
   */
  get customApplicationFieldRows() {
    return this.customApplicationFields
      .filter((field: CustomFieldSignature) => field.visible)
      .map((field: CustomFieldSignature) => {
        const values = field.values || [];
        if (values.length === 0) return null;

        // Format value(s)
        let formattedValue = '';
        if (values.length > 1) {
          formattedValue = values
            .map((value) => this.formatCustomFieldValue(value, field))
            .join(', ');
        } else if (values[0] !== undefined) {
          formattedValue = this.formatCustomFieldValue(values[0], field);
        }

        return {
          label: field.label || field.name || '',
          value: formattedValue,
        };
      })
      .filter((row: { label: string; value: string } | null) => row !== null);
  }

  /**
   * Get formatted custom application field rows for read-only display
   * @deprecated Use province-specific getters instead: customEmployeeFieldRows, customProgramFieldRows, customApplicationFieldRows
   * All custom fields on the program instance
   */
  get customApplicationDetailFieldRows() {
    const customFields = this.customApplicationDetailFields;
    if (!customFields || customFields.length === 0) return [];

    /**
     * Format a single value based on field type
     */
    const formatValue = (value: string | boolean | number, field: CustomFieldSignature): string => {
      // First check if there's an option mapping (for select/radio/checkbox fields)
      const option = field.options?.find((opt: CustomFieldOption) => opt.value === value);
      if (option) {
        return option.label;
      }

      // Format based on inputType
      const inputType = field.inputType;

      if (inputType === 'currency') {
        // Value is in cents, convert to dollars
        const numValue = Number(value);
        if (isNaN(numValue)) return String(value);
        return `$${(numValue / 100).toFixed(2)}`;
      }

      if (inputType === 'date') {
        // Value is in format "YYYY-MM-DD", convert to "MM/DD/YYYY" using UTC
        return formatISODateToDisplay(String(value), this.intl, 'numeric');
      }

      // Default: return as string
      return String(value);
    };

    return customFields
      .filter((field: CustomFieldSignature) => field.visible)
      .map((field: CustomFieldSignature) => {
        const values = field.values || [];
        if (values.length === 0) return null;

        // Format value(s)
        let formattedValue = '';
        if (values.length > 1) {
          formattedValue = values.map((value) => formatValue(value, field)).join(', ');
        } else if (values[0] !== undefined) {
          formattedValue = formatValue(values[0], field);
        }

        return {
          label: field.label || field.name || '',
          value: formattedValue,
        };
      })
      .filter((row: { label: string; value: string } | null) => row !== null);
  }

  get expenseRows() {
    const courses = this.args.model.activeCourses || [];

    // Get course expenses
    const isCreditBased = this.args.model.isCreditBased;
    const courseRows = courses.map((course, index) => {
      const courseName = course.fields?.COURSE_NAME?.values?.[0] || `Course ${index + 1}`;
      let amount: string;
      if (isCreditBased) {
        const credits = course.courseCredit || 0;
        amount = `${credits} credits`;
      } else {
        const courseCost = course.courseExpenses;
        amount = courseCost ? `$${(courseCost / 100).toFixed(2)}` : '$0.00';
      }

      return {
        label: courseName,
        amount: amount,
        isTotal: false,
        type: 'course' as const,
        courseIndex: index,
      };
    });

    const rows: ExpenseRow[] = [...courseRows];

    // Add scholarships if present
    const scholarshipsTotal = this.args.model.scholarshipsTotal || 0;
    if (scholarshipsTotal > 0) {
      rows.push({
        label: 'Scholarships',
        amount: `-$${(scholarshipsTotal / 100).toFixed(2)}`,
        isTotal: false,
        type: 'scholarship' as const,
      });
    }

    // Add course cash total if using course cash payment method
    const isCourseCashPayment =
      this.args.model.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
    const courseCashTotal = Number(
      this.args.model.requestedCourseCashSubtotalLessScholarships || 0
    );
    if (isCourseCashPayment && courseCashTotal > 0) {
      rows.push({
        label: 'Course Cash Total',
        amount: `$${(courseCashTotal / 100).toFixed(2)}`,
        isTotal: false,
        type: 'course-cash' as const,
      });
    }

    // Add total requested (label changes based on whether course cash is shown)
    const totalLabel =
      isCourseCashPayment && courseCashTotal > 0
        ? 'Total Reimbursement Requested'
        : 'Total Requested';
    rows.push({
      label: totalLabel,
      amount: this.requestedAmount,
      isTotal: true,
      type: 'total' as const,
    });

    return rows;
  }

  /**
   * Check if user can edit course expenses
   */
  get canEditCourse() {
    // @ts-expect-error: SessionContext user property not typed in common
    return this.sessionContext.canEditTasApplication;
  }

  /**
   * Check if the currently selected course is being edited
   */
  get isEditingCourseExpenses() {
    return this.editingCourseExpensesIndex === this.selectedCourseIndex;
  }

  /**
   * Get list of editable expense field names
   */
  get editableExpenseFields() {
    return ['COURSE_TUITION', 'COURSE_BOOKS', 'COURSE_LABS', 'COURSE_SOFTWARE', 'COURSE_FEES'];
  }

  /**
   * Get course expense value for editing
   * Converts cents to dollars for display in currency input
   */
  getCourseExpenseValue = (fieldName: string): string => {
    if (!this.courseExpenseFieldsCopy) return '0';
    const value = getSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      this.courseExpenseFieldsCopy
    );
    // Convert cents to dollars for display (divide by 100)
    // Default to "0" if no value
    return value ? String(Number(value) / 100) : '0';
  };

  /**
   * Validate expense changelog is not empty
   */
  get expenseChangelogValidationErrors(): string[] {
    const schema = v.pipe(v.string(), v.minLength(1, 'Comment is required'));
    const result = v.safeParse(schema, this.expenseChangelog);
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  /**
   * Check if expense form is valid (no validation errors and changelog is filled)
   */
  get isExpenseFormValid(): boolean {
    return this.expenseChangelogValidationErrors.length === 0;
  }

  // ===== Course Detail Editing Methods =====

  toggleCourseDetailEdit = () => {
    const isCurrentlyEditing = this.editingCourseDetailsIndex === this.selectedCourseIndex;

    if (isCurrentlyEditing) {
      // Cancel - discard changes
      this.editingCourseDetailsIndex = null;
      this.courseDetailFieldsCopy = null;
      this.courseCustomFieldsCopy = null;
      this.detailChangelog = '';
    } else {
      // Enter edit mode
      const course = this.selectedCourse;
      if (!course) return;

      // Create tracked copies
      this.courseDetailFieldsCopy = trackedObject(course.fields);
      this.courseCustomFieldsCopy = course.customFields
        ? JSON.parse(JSON.stringify(course.customFields))
        : [];
      this.detailChangelog = '';
      this.editingCourseDetailsIndex = this.selectedCourseIndex;
    }
  };

  updateCourseDetailField = (fieldName: string, value: string | number) => {
    if (!this.courseDetailFieldsCopy) return;

    let valueToSet: string | number = value;

    // Handle numeric fields
    if (fieldName === 'COURSE_CREDIT') {
      valueToSet = Math.round(Number(value));
    }

    setSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      valueToSet,
      this.courseDetailFieldsCopy
    );
  };

  getCourseDetailValue = (fieldName: string): string => {
    if (!this.courseDetailFieldsCopy) return '';
    const value = getSingleValueForTasField(
      fieldName as keyof TasCourseModel['fields'],
      this.courseDetailFieldsCopy
    );
    return value ? String(value) : '';
  };

  updateDetailChangelog = (value: string) => {
    this.detailChangelog = value;
  };

  saveCourseDetails = dropTask(async () => {
    const course = this.selectedCourse;
    if (!course || !this.courseDetailFieldsCopy) {
      return;
    }

    // Validate changelog before saving
    if (!this.isDetailFormValid) {
      return;
    }

    try {
      // Update course with edited detail fields and custom fields
      course.fields = this.courseDetailFieldsCopy;
      if (this.courseCustomFieldsCopy) {
        course.customFields = this.courseCustomFieldsCopy;
      }

      // Save with changelog
      await course.save({ adapterOptions: { changelog: this.detailChangelog } });

      // No recalculation needed for detail fields (unlike expenses)
      // Refresh to show updated data
      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();

      // Exit edit mode and clear tracked copies
      this.editingCourseDetailsIndex = null;
      this.courseDetailFieldsCopy = null;
      this.courseCustomFieldsCopy = null;
      this.detailChangelog = '';

      // Refresh display
      this.courseDetailsFor(course);
    } catch (error) {
      console.error('Error saving course details:', error);
      // TODO: Show error notification to user
    }
  });

  /**
   * Validate detail changelog is not empty
   */
  get detailChangelogValidationErrors(): string[] {
    const schema = v.pipe(v.string(), v.minLength(1, 'Comment is required'));
    const result = v.safeParse(schema, this.detailChangelog);
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  /**
   * Check if detail form is valid (no validation errors and changelog is filled)
   * Validates both the changelog and all course custom fields
   */
  get isDetailFormValid(): boolean {
    // Check changelog validation
    if (this.detailChangelogValidationErrors.length > 0) {
      return false;
    }

    // Check all course custom fields for validation errors
    if (this.courseCustomFieldsCopy) {
      for (const field of this.courseCustomFieldsCopy) {
        const errors = this.getCourseCustomFieldValidationErrors(field.name || '');
        if (errors.length > 0) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Get validation errors for a specific course custom field
   * Currently validates only required fields
   * @param fieldName - The name of the custom field
   * @returns Array of error messages for this field
   */
  getCourseCustomFieldValidationErrors = (fieldName: string): string[] => {
    if (!this.courseCustomFieldsCopy) return [];

    const field = this.courseCustomFieldsCopy.find((f) => f.name === fieldName);
    if (!field) return [];

    const errors: string[] = [];

    // Check required field validation
    if (field.required) {
      const hasValue =
        field.values &&
        field.values.length > 0 &&
        field.values[0] !== null &&
        field.values[0] !== undefined &&
        field.values[0] !== '';

      if (!hasValue) {
        errors.push(`${field.label || fieldName} is required`);
      }
    }

    // Future: Add more validation rules here (min/max length, patterns, etc.)

    return errors;
  };

  /**
   * Get form validation errors for course detail fields
   * Returns empty array for now as we don't have field-level validation yet
   */
  get courseDetailFormValidationErrors(): ValidationErrorSignature[] {
    return [];
  }

  /**
   * Check if the currently selected course details are being edited
   */
  get isEditingCourseDetails() {
    return this.editingCourseDetailsIndex === this.selectedCourseIndex;
  }

  /**
   * Get grade options for the selected course
   * Checks program template for field options or custom field inline options
   */
  get gradeOptions() {
    const course = this.selectedCourse;
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;

    if (!course || !programTemplate) return [];

    // Check if COURSE_GRADE is a standard field with tasFieldOptions
    const templateFields = programTemplate.fields as
      | Record<string, { value_as_field_option?: boolean }>
      | undefined;
    const gradeConfig = templateFields?.['COURSE_GRADE'];

    if (gradeConfig?.value_as_field_option) {
      // Get options from program template tasFieldOptions
      return getSortedOptionsByFieldName('COURSE_GRADE', programTemplate.tasFieldOptions);
    }

    // Check custom fields for inline options
    const customField = course.customFields?.find((f) => f.name === 'COURSE_GRADE');
    if (customField) {
      // Check if custom field uses tasFieldOptions
      if (customField.value_as_field_option) {
        return getSortedOptionsByFieldName('COURSE_GRADE', programTemplate.tasFieldOptions);
      }
      // Use inline options
      return customField.options || [];
    }

    return [];
  }

  /**
   * Get list of editable detail field names
   */
  get editableCourseDetailFields() {
    return ['COURSE_NAME', 'COURSE_NUMBER', 'COURSE_DESCRIPTION', 'COURSE_CREDIT', 'COURSE_GRADE'];
  }

  // ===== Application Details Editing Methods =====

  /**
   * Check if user can edit application details
   */
  get canEditApplicationDetails() {
    // @ts-expect-error: SessionContext user property not typed in common
    return this.sessionContext.canEditTasApplication;
  }

  /**
   * Determine which model a field belongs to
   */
  getFieldSource(fieldName: string): 'application' | 'programInstance' {
    const appFields = APPLICATION_FIELD_CONFIGS.map((c) => c.name);
    return appFields.includes(fieldName as (typeof appFields)[number])
      ? 'application'
      : 'programInstance';
  }

  /**
   * Handle updates to APPLICATION province custom fields
   * Updates only the tracked copy during edit mode
   */
  didUpdateApplicationCustomFields = (customFields: CustomFieldSignature[]) => {
    if (!this.applicationCustomFieldsCopy) return;

    const updatedFields = this.updateFieldsWithNewValues(
      this.applicationCustomFieldsCopy,
      customFields
    );

    this.applicationCustomFieldsCopy = updatedFields;
  };

  /**
   * Handle updates to EMPLOYEE province custom fields
   * Updates only the tracked copy during edit mode
   */
  didUpdateEmployeeCustomFields = (customFields: CustomFieldSignature[]) => {
    if (!this.programInstanceCustomFieldsCopy) return;

    const updatedFields = this.updateFieldsWithNewValues(
      this.programInstanceCustomFieldsCopy,
      customFields
    );

    this.programInstanceCustomFieldsCopy = updatedFields;
  };

  /**
   * Handle updates to PROGRAM province custom fields
   * Updates only the tracked copy during edit mode
   */
  didUpdateProgramCustomFields = (customFields: CustomFieldSignature[]) => {
    if (!this.programInstanceCustomFieldsCopy) return;

    const updatedFields = this.updateFieldsWithNewValues(
      this.programInstanceCustomFieldsCopy,
      customFields
    );

    this.programInstanceCustomFieldsCopy = updatedFields;
  };

  /**
   * Handler for course custom field updates from drawer component
   * Updates the tracked copy during edit mode
   */
  didUpdateCourseCustomFields = (customFields: CustomFieldSignature[]) => {
    if (!this.courseCustomFieldsCopy) return;

    const updatedFields = this.updateFieldsWithNewValues(this.courseCustomFieldsCopy, customFields);

    this.courseCustomFieldsCopy = updatedFields;
  };

  /**
   * Toggle application details edit mode
   */
  toggleApplicationDetailsEdit = async () => {
    if (this.isEditingApplicationDetails) {
      // Cancel - discard changes
      this.isEditingApplicationDetails = false;
      this.applicationFieldsCopy = null;
      this.applicationCustomFieldsCopy = null;
      this.programInstanceFieldsCopy = null;
      this.programInstanceCustomFieldsCopy = null;
      this.applicationDetailsChangelog = '';
      this.selectedSchool = undefined;
      this.hasSubmittedApplicationDetails = false;
      this.courseDateValidationErrors = {};
    } else {
      // Enter edit mode - create tracked copies
      const programInstance = this.args.model.tasProgramInstance;
      if (!programInstance) {
        console.error('No program instance found');
        return;
      }

      // Create tracked copies of fields
      this.applicationFieldsCopy = trackedObject(this.args.model.fields);
      this.programInstanceFieldsCopy = trackedObject(programInstance.fields);

      // Create deep copies of custom fields arrays to track changes
      this.applicationCustomFieldsCopy = this.args.model.customFields
        ? JSON.parse(JSON.stringify(this.args.model.customFields))
        : [];
      this.programInstanceCustomFieldsCopy = programInstance.customFields
        ? JSON.parse(JSON.stringify(programInstance.customFields))
        : [];

      this.applicationDetailsChangelog = '';
      this.hasSubmittedApplicationDetails = false;

      // Load previously selected school if available
      try {
        const tasInstitutionId = programInstance.fields['TAS_INSTITUTION_ID']?.values?.[0];
        const dapipInstitutionId = programInstance.fields['DAPIP_INSTITUTION_ID']?.values?.[0];

        if (tasInstitutionId || dapipInstitutionId) {
          const filter: Record<string, string | number | undefined> = {
            'tas-program-template': programInstance.tasProgramTemplate.id!,
          };

          if (dapipInstitutionId) {
            filter['dapip-id'] = dapipInstitutionId;
          }

          if (tasInstitutionId) {
            filter['institution-id'] = tasInstitutionId;
          }

          const results = await this.store.query<TasUnifiedInstitutionModel>(
            'tas-unified-institution',
            {
              // @ts-expect-error - filter type mismatch
              filter,
              page: { limit: 1, offset: 0 },
            }
          );

          if (results.length > 0) {
            this.selectedSchool = results[0];
          }
        }
      } catch (e) {
        console.error('Error attempting to load previously selected school', e);
      }

      this.isEditingApplicationDetails = true;

      // Run initial validation for existing date values
      this.validateCourseDates();
    }
  };

  /**
   * Type guards for TasUnifiedInstitution properties
   */
  private hasValidLocationName(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { locationName: string } {
    return !!(institution && 'locationName' in institution && institution.locationName);
  }

  private hasValidInstitutionId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { institutionId: string } {
    return !!(institution && 'institutionId' in institution && institution.institutionId);
  }

  private hasValidDapipId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { dapipId: string } {
    return !!(institution && 'dapipId' in institution && institution.dapipId);
  }

  /**
   * Handle school selection from SchoolSelect component
   */
  didSelectSchool = (searchResult?: TasUnifiedInstitution) => {
    this.selectedSchool = searchResult as unknown as TasUnifiedInstitutionModel;

    if (!searchResult || !this.programInstanceFieldsCopy) {
      return;
    }

    // Update SCHOOL_INSTITUTION_NAME
    setSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      this.hasValidLocationName(searchResult) ? searchResult.locationName : '',
      this.programInstanceFieldsCopy
    );

    // Update TAS_INSTITUTION_ID
    setSingleValueForTasField(
      'TAS_INSTITUTION_ID',
      this.hasValidInstitutionId(searchResult) ? searchResult.institutionId : '',
      this.programInstanceFieldsCopy
    );

    // Update DAPIP_INSTITUTION_ID
    setSingleValueForTasField(
      'DAPIP_INSTITUTION_ID',
      this.hasValidDapipId(searchResult) ? searchResult.dapipId : '',
      this.programInstanceFieldsCopy
    );
  };

  /**
   * Update application detail field value
   */
  updateApplicationDetailField = (
    fieldName: string,
    value?: string | string[] | number,
    event?: Event
  ) => {
    const source = this.getFieldSource(fieldName);
    const fieldsCopy =
      source === 'application' ? this.applicationFieldsCopy : this.programInstanceFieldsCopy;

    if (!fieldsCopy) return;

    let valueToSet: string | number = Array.isArray(value) ? (value[0] ?? '') : (value ?? '');

    // @ts-expect-error - inputmask types not fully defined
    if (event?.target?.inputmask?.userOptions?.alias === 'currency') {
      // @ts-expect-error - inputmask types not fully defined
      valueToSet = event.target.inputmask?.unmaskedvalue?.() * 100;
    }

    // Use type assertion - TypeScript can't infer the relationship between fieldName and fieldsCopy
    if (source === 'application') {
      setSingleValueForTasField(
        fieldName as keyof TasApplicationModel['fields'],
        valueToSet,
        fieldsCopy as TasApplicationModel['fields']
      );
    } else {
      setSingleValueForTasField(
        fieldName as keyof TasProgramInstanceModel['fields'],
        valueToSet,
        fieldsCopy as TasProgramInstanceModel['fields']
      );
    }
  };

  /**
   * Wrapper for updating date fields from EmberFlatpickr
   * Converts Date[] to ISO string and calls updateApplicationDetailField
   * Also triggers validation and updates SEMESTER_CODE for begin date
   */
  updateDateField = (fieldName: string, selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      this.updateApplicationDetailField(fieldName, isoDate);

      // Update SEMESTER_CODE if begin date changed and field is visible
      if (fieldName === 'COURSES_BEGIN_DATE') {
        const template = this.args.model.tasProgramInstance?.tasProgramTemplate;
        if (template?.semesterCodeIsVisible) {
          const semesterCode = setSemesterCodeString(isoDate);
          this.updateApplicationDetailField('SEMESTER_CODE', semesterCode);
        }
      }

      // Trigger validation after date change
      this.validateCourseDates();
    }
  };

  /**
   * Get application detail field value for editing
   */
  getApplicationDetailValue = (fieldName: string): string => {
    const source = this.getFieldSource(fieldName);
    const fieldsCopy =
      source === 'application' ? this.applicationFieldsCopy : this.programInstanceFieldsCopy;

    if (!fieldsCopy) return '';

    let value: string | number | boolean | null;
    if (source === 'application') {
      value = getSingleValueForTasField(
        fieldName as keyof TasApplicationModel['fields'],
        fieldsCopy as TasApplicationModel['fields']
      );
    } else {
      value = getSingleValueForTasField(
        fieldName as keyof TasProgramInstanceModel['fields'],
        fieldsCopy as TasProgramInstanceModel['fields']
      );
    }

    // Handle currency fields - convert cents to dollars for display
    if (fieldName === 'ESTIMATED_COST' && value) {
      return String(Number(value) / 100);
    }

    return value ? String(value) : '';
  };

  /**
   * Update custom program field value
   * Updates the tracked copy of custom fields for reactive editing
   */
  updateCustomProgramField = (fieldName: string, updatedField: CustomFieldSignature) => {
    if (!this.programInstanceCustomFieldsCopy) return;

    const customFieldIndex = this.programInstanceCustomFieldsCopy.findIndex(
      (f) => f.name === fieldName
    );
    if (customFieldIndex >= 0) {
      this.programInstanceCustomFieldsCopy[customFieldIndex] = updatedField;
    }
  };

  /**
   * Update application details changelog
   */
  updateApplicationDetailsChangelog = (value: string) => {
    this.applicationDetailsChangelog = value;
  };

  /**
   * Validate application details changelog
   */
  get applicationDetailsChangelogValidationErrors(): string[] {
    const schema = v.pipe(v.string(), v.minLength(1, 'Comment is required'));
    const result = v.safeParse(schema, this.applicationDetailsChangelog);
    return result.success ? [] : result.issues.map((issue) => issue.message);
  }

  /**
   * Get flattened errors compatible with errorsForValibotField helper
   */
  get courseDateFormValidationErrors(): Record<string, readonly string[] | undefined> {
    if (!this.isEditingApplicationDetails) {
      return {};
    }

    const beginDateErrors = this.courseDateValidationErrors['COURSES_BEGIN_DATE'];
    const endDateErrors = this.courseDateValidationErrors['COURSES_END_DATE'];

    return {
      COURSES_BEGIN_DATE: beginDateErrors?.length ? beginDateErrors : undefined,
      COURSES_END_DATE: endDateErrors?.length ? endDateErrors : undefined,
    };
  }

  /**
   * Get field config from template for date validation
   */
  getDateFieldConfig = (fieldName: 'COURSES_BEGIN_DATE' | 'COURSES_END_DATE'): FieldConfig => {
    const template = this.args.model.tasProgramInstance?.tasProgramTemplate;
    const fieldConfig = template?.fields?.[fieldName];

    return {
      visible: fieldConfig?.visible ?? false,
      required: fieldConfig?.required ?? false,
    };
  };

  /**
   * Validate course dates
   * Runs validation for both COURSES_BEGIN_DATE and COURSES_END_DATE
   */
  validateCourseDates = () => {
    if (!this.isEditingApplicationDetails) {
      this.courseDateValidationErrors = {};
      return;
    }

    const template = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!template) {
      this.courseDateValidationErrors = {};
      return;
    }

    const beginDate = this.getApplicationDetailValue('COURSES_BEGIN_DATE');
    const endDate = this.getApplicationDetailValue('COURSES_END_DATE');

    const beginFieldConfig = this.getDateFieldConfig('COURSES_BEGIN_DATE');
    const endFieldConfig = this.getDateFieldConfig('COURSES_END_DATE');

    // Get employment start date from tasParticipant (not HIRE_DATE field)
    const employmentStartDate =
      this.args.model.tasProgramInstance?.employee?.tasParticipant?.employmentStartDate;
    const eligibilityWaitingPeriod = template.eligibilityWaitingPeriod;

    this.courseDateValidationErrors = {
      COURSES_BEGIN_DATE: validateBeginDate(
        beginDate,
        beginFieldConfig,
        template,
        this.intl,
        employmentStartDate,
        eligibilityWaitingPeriod
      ),
      COURSES_END_DATE: validateEndDate(
        endDate,
        beginDate,
        endFieldConfig,
        template.isCertificateProgram ?? false
      ),
    };
  };

  /**
   * Check if application details form is valid
   * Validates changelog, required standard fields, and all custom fields
   * Note: Date validation errors are shown as warnings but don't block submission
   */
  get isApplicationDetailsFormValid(): boolean {
    // Check changelog validation
    if (this.applicationDetailsChangelogValidationErrors.length > 0) {
      return false;
    }

    // Date validation errors are displayed but don't block submission
    // Only missing required fields and missing changelog should prevent submission

    // Check required standard TAS fields (from program template)
    const template = this.args.model.tasProgramInstance?.tasProgramTemplate;
    const templateFields = template?.fields as ProgramTemplateFields | undefined;

    if (templateFields) {
      // Check application fields
      for (const fieldConfig of APPLICATION_FIELD_CONFIGS) {
        const fieldName = fieldConfig.name;
        const fieldMeta = templateFields[fieldName];

        if (fieldMeta?.required) {
          const value = this.getApplicationDetailValue(fieldName);
          if (!value || value === '') {
            return false;
          }
        }
      }

      // Check program instance fields
      for (const fieldConfig of PROGRAM_INSTANCE_FIELD_CONFIGS) {
        const fieldName = fieldConfig.name;
        const fieldMeta = templateFields[fieldName];

        if (fieldMeta?.required) {
          const value = this.getApplicationDetailValue(fieldName);
          if (!value || value === '') {
            return false;
          }
        }
      }
    }

    // Check all custom fields for validation errors
    const allFields = [
      ...(this.programInstanceCustomFieldsCopy || []),
      ...(this.applicationCustomFieldsCopy || []),
    ];

    for (const field of allFields) {
      const errors = this.getCustomFieldValidationErrors(field.name || '');
      if (errors.length > 0) {
        return false;
      }
    }

    return true;
  }

  /**
   * Get validation errors for a specific custom field
   * Currently validates only required fields
   * @param fieldName - The name of the custom field
   * @returns Array of error messages for this field
   */
  getCustomFieldValidationErrors = (fieldName: string): string[] => {
    const allFields = [
      ...(this.programInstanceCustomFieldsCopy || []),
      ...(this.applicationCustomFieldsCopy || []),
    ];

    const field = allFields.find((f) => f.name === fieldName);
    if (!field) return [];

    const errors: string[] = [];

    // Check required field validation
    if (field.required) {
      const hasValue =
        field.values &&
        field.values.length > 0 &&
        field.values[0] !== null &&
        field.values[0] !== undefined &&
        field.values[0] !== '';

      if (!hasValue) {
        errors.push(`${field.label || fieldName} is required`);
      }
    }

    // Future: Add more validation rules here (min/max length, patterns, etc.)

    return errors;
  };

  /**
   * Get validation errors for all custom fields across all provinces
   * Returns array in format expected by TuitionAssistanceFormsCustomFieldsForProvince
   * Uses existing getCustomFieldValidationErrors method for actual validation
   */
  get customFieldValidationErrors(): ValidationErrorSignature[] {
    // Only validate during edit mode
    if (!this.isEditingApplicationDetails) {
      return [];
    }

    const allFields = [
      ...this.customEmployeeFields,
      ...this.customProgramFields,
      ...this.customApplicationFields,
    ];

    const errors: ValidationErrorSignature[] = [];

    allFields.forEach((field) => {
      const fieldErrors = this.getCustomFieldValidationErrors(field.name || '');
      if (fieldErrors.length > 0) {
        errors.push({
          path: field.label || field.name || '',
          value: field.values?.[0]?.toString() || '',
          errors: fieldErrors,
        });
      }
    });

    return errors;
  }

  /**
   * Get validation errors for course custom fields
   * Filters errors to only COURSE province fields
   */
  get courseCustomFieldValidationErrors(): ValidationErrorSignature[] {
    if (!this.courseCustomFieldsCopy) return [];

    return this.courseCustomFieldsCopy
      .filter((field) => field.province === 'COURSE')
      .map((field) => ({
        path: field.label || field.name || '',
        value: field.values?.[0]?.toString() || '',
        errors: this.getCourseCustomFieldValidationErrors(field.name || ''),
      }))
      .filter((error) => error.errors.length > 0);
  }

  /**
   * Get custom application fields from both program instance and application
   * Uses tracked copies when in edit mode, otherwise uses live model data
   */
  get customApplicationDetailFields() {
    // When editing, use tracked copies; otherwise use live model data
    if (this.isEditingApplicationDetails) {
      const programInstanceFields = this.programInstanceCustomFieldsCopy || [];
      const applicationFields = this.applicationCustomFieldsCopy || [];

      return [...programInstanceFields, ...applicationFields].sort(
        (a, b) => (a.ordinal || 0) - (b.ordinal || 0)
      );
    }

    const programInstance = this.args.model.tasProgramInstance;
    const application = this.args.model;

    // Combine custom fields from both program instance and application
    const programInstanceFields = programInstance?.customFields || [];
    const applicationFields = application?.customFields || [];

    return [...programInstanceFields, ...applicationFields].sort(
      (a, b) => (a.ordinal || 0) - (b.ordinal || 0)
    );
  }

  /**
   * Get custom course fields (province: COURSE) for a specific course
   * Used in course details drawer
   * Returns tracked copy when editing, otherwise returns live model data
   */
  courseCustomFieldsFor = (course: TasCourseModel): CustomFieldSignature[] => {
    // When editing this specific course, use tracked copy
    if (
      this.isEditingCourseDetails &&
      course === this.selectedCourse &&
      this.courseCustomFieldsCopy
    ) {
      return this.courseCustomFieldsCopy
        .filter((field) => field.visible !== false)
        .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
    }

    // Otherwise use live model data
    if (!course?.customFields) return [];

    return course.customFields
      .filter((field) => field.visible !== false) // Show by default unless explicitly hidden
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0)) as CustomFieldSignature[];
  };

  /**
   * Get visible program instance field configs
   * Filters PROGRAM_INSTANCE_FIELD_CONFIGS to only include fields marked visible in program template
   */
  get visibleProgramInstanceFieldConfigs() {
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!programTemplate) return [];

    const templateFields = programTemplate.fields as ProgramTemplateFields;

    return PROGRAM_INSTANCE_FIELD_CONFIGS.filter((config) => {
      const fieldConfig = templateFields?.[config.name];
      return fieldConfig?.visible === true;
    });
  }

  /**
   * Get visible application field configs
   * Filters APPLICATION_FIELD_CONFIGS to only include fields marked visible in program template
   */
  get visibleApplicationFieldConfigs() {
    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;
    if (!programTemplate) return [];

    const templateFields = programTemplate.fields as ProgramTemplateFields;

    return APPLICATION_FIELD_CONFIGS.filter((config) => {
      const fieldConfig = templateFields?.[config.name];
      return fieldConfig?.visible === true;
    });
  }

  /**
   * Save application details changes
   */
  saveApplicationDetails = dropTask(async () => {
    // Set submission flag to show validation errors
    this.hasSubmittedApplicationDetails = true;

    // Run validation before checking validity
    this.validateCourseDates();

    if (!this.isApplicationDetailsFormValid) {
      return;
    }

    const programInstance = this.args.model.tasProgramInstance;
    if (!programInstance) {
      console.error('No program instance found');
      return;
    }

    try {
      // Update models with edited fields and custom fields
      if (this.applicationFieldsCopy) {
        this.args.model.fields = this.applicationFieldsCopy;
      }
      if (this.applicationCustomFieldsCopy) {
        this.args.model.customFields = this.applicationCustomFieldsCopy;
      }
      if (this.programInstanceFieldsCopy) {
        programInstance.fields = this.programInstanceFieldsCopy;
      }
      if (this.programInstanceCustomFieldsCopy) {
        programInstance.customFields = this.programInstanceCustomFieldsCopy;
      }

      // Save both models with changelog
      const adapterOptions = { changelog: this.applicationDetailsChangelog };

      // Save program instance if it has changes
      if (this.programInstanceFieldsCopy || this.programInstanceCustomFieldsCopy) {
        await programInstance.save({ adapterOptions });
      }

      // Save application if it has changes
      if (this.applicationFieldsCopy || this.applicationCustomFieldsCopy) {
        await this.args.model.save({ adapterOptions });
      }

      // Refresh data
      this.tuitionAssistance.refreshActiveLimitsSummaryComponents();

      // Exit edit mode and clear all tracked copies
      this.isEditingApplicationDetails = false;
      this.applicationFieldsCopy = null;
      this.applicationCustomFieldsCopy = null;
      this.programInstanceFieldsCopy = null;
      this.programInstanceCustomFieldsCopy = null;
      this.applicationDetailsChangelog = '';
      this.selectedSchool = undefined;
      this.hasSubmittedApplicationDetails = false;
      this.courseDateValidationErrors = {};
    } catch (error) {
      console.error('Error saving application details:', error);
      // Don't reset hasSubmittedApplicationDetails on error so errors stay visible
    }
  });

  /**
   * Get the expense type label from the application's EXPENSE_TYPE field
   * Uses the application model's expenseType getter, falls back to "Courses"
   */
  get expenseType(): string {
    const expenseType = this.args.model.tasProgramInstance?.tasProgramTemplate?.expenseType;
    return expenseType || 'Courses';
  }

  /**
   * Get the tab label for a course
   * Uses application's expenseType with index, falls back to "Course #"
   */
  getTabLabel = (index: number): string => {
    const appExpenseType = this.expenseType;
    return appExpenseType ? `${appExpenseType} ${index + 1}` : `Course ${index + 1}`;
  };

  /**
   * Get formatted expense items for a specific course
   * Uses field labels from program template for display
   */
  courseExpensesFor = (course: TasCourseModel) => {
    if (!course) return [];

    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;

    // Helper to get field label from program template or use fallback
    const getFieldLabel = (fieldName: string, fallback: string): string => {
      // For standard fields, get label from program template
      const fields = course.fields as Record<string, { values: unknown[] }> | undefined;
      if (fields?.[fieldName] && programTemplate) {
        const templateFields = programTemplate.fields as ProgramTemplateFields;
        const config = templateFields?.[fieldName];
        if (config?.label) return config.label;
      }

      // For custom fields, label is on the field itself
      const customField = course.customFields?.find((f) => f.name === fieldName);
      if (customField?.label) return customField.label;

      return fallback;
    };

    const expenses = [
      {
        label: getFieldLabel('COURSE_TUITION', 'Tuition!'),
        amount: course.courseTuition ? `$${(course.courseTuition / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_BOOKS', 'Books'),
        amount: course.courseBooks ? `$${(course.courseBooks / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_LABS', 'Labs'),
        amount: course.courseLabs ? `$${(course.courseLabs / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_SOFTWARE', 'Software'),
        amount: course.courseSoftware ? `$${(course.courseSoftware / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: getFieldLabel('COURSE_FEES', 'Fees'),
        amount: course.courseFees ? `$${(course.courseFees / 100).toFixed(2)}` : '$0.00',
        isTotal: false,
      },
      {
        label: 'Total',
        amount: course.courseExpenses ? `$${(course.courseExpenses / 100).toFixed(2)}` : '$0.00',
        isTotal: true,
      },
    ];

    return expenses;
  };

  /**
   * Get formatted detail rows for a specific course
   * Uses field labels from program template for display
   * Includes custom fields from customFields array
   */
  courseDetailsFor = (course: TasCourseModel) => {
    if (!course) return [];

    const programTemplate = this.args.model.tasProgramInstance?.tasProgramTemplate;

    // Helper to get field label from program template or use fallback
    const getFieldLabel = (fieldName: string, fallback: string): string => {
      // For standard fields, get label from program template
      const fields = course.fields as Record<string, { values: unknown[] }> | undefined;
      if (fields?.[fieldName] && programTemplate) {
        const templateFields = programTemplate.fields as ProgramTemplateFields;
        const config = templateFields?.[fieldName];
        if (config?.label) return config.label;
      }

      // For custom fields, label is on the field itself
      const customField = course.customFields?.find((f) => f.name === fieldName);
      if (customField?.label) return customField.label;

      return fallback;
    };

    // Helper to get field value from fields or customFields
    const getFieldValue = (fieldName: string): string | number | boolean | null => {
      // Try standard fields
      const fields = course.fields as Record<string, { values: unknown[] }> | undefined;
      const value = fields?.[fieldName]?.values?.[0];
      if (value !== undefined) return value as string | number | boolean;

      // Try custom fields
      const customField = course.customFields?.find((f) => f.name === fieldName);
      return customField?.values?.[0] || null;
    };

    const details = [];

    // Course Name - show if has value
    const courseName = course.courseName || getFieldValue('COURSE_NAME');
    if (courseName) {
      details.push({
        label: getFieldLabel('COURSE_NAME', 'Course Name'),
        value: String(courseName),
      });
    }

    // Course Number - show if has value
    const courseNumber = course.courseNumber || getFieldValue('COURSE_NUMBER');
    if (courseNumber) {
      details.push({
        label: getFieldLabel('COURSE_NUMBER', 'Course Number'),
        value: String(courseNumber),
      });
    }

    // Course Description - show if has value
    const courseDescription = course.courseDescription || getFieldValue('COURSE_DESCRIPTION');
    if (courseDescription) {
      details.push({
        label: getFieldLabel('COURSE_DESCRIPTION', 'Course Description'),
        value: String(courseDescription),
      });
    }

    // Credits - show if has value
    const credits = course.courseCredit || getFieldValue('COURSE_CREDIT');
    if (credits) {
      details.push({
        label: getFieldLabel('COURSE_CREDIT', 'Credits'),
        value: String(credits),
      });
    }

    // Grade - show if has value and grades are enabled
    if (course.hasGrades) {
      const grade = course.courseGrade || getFieldValue('COURSE_GRADE');
      if (grade) {
        details.push({
          label: getFieldLabel('COURSE_GRADE', 'Grade'),
          value: String(grade),
        });
      }
    }

    // Add custom fields from customFields array
    // Filter for visible fields, sort by ordinal
    const customFields = (course.customFields || [])
      .filter((field) => field.visible !== false) // Show by default unless explicitly hidden
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));

    /**
     * Format a single value based on field type
     */
    const formatCustomFieldValue = (
      value: string | boolean | number,
      field: CustomFieldSignature
    ): string => {
      // First check if there's an option mapping (for select/radio/checkbox fields)
      const option = field.options?.find((opt: CustomFieldOption) => opt.value === value);
      if (option) {
        return option.label;
      }

      // Format based on inputType
      const inputType = field.inputType;

      if (inputType === 'currency') {
        // Value is in cents, convert to dollars
        const numValue = Number(value);
        if (isNaN(numValue)) return String(value);
        return `$${(numValue / 100).toFixed(2)}`;
      }

      if (inputType === 'date') {
        // Value is in format "YYYY-MM-DD", convert to "MM/DD/YYYY"
        const dateStr = String(value);
        try {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date.toLocaleDateString('en-US');
          }
        } catch {
          // If parsing fails, return as-is
        }
        return dateStr;
      }

      // Default: return as string
      return String(value);
    };

    customFields.forEach((field) => {
      const values = field.values || [];
      if (values.length === 0) return;

      // Format value(s) based on inputType
      let formattedValue = '';
      if (values.length > 1) {
        formattedValue = values.map((value) => formatCustomFieldValue(value, field)).join(', ');
      } else if (values[0] !== undefined) {
        formattedValue = formatCustomFieldValue(values[0], field);
      }

      if (formattedValue) {
        details.push({
          label: field.label || field.name || '',
          value: formattedValue,
        });
      }
    });

    return details;
  };

  <template>
    {{pageTitle (t "tuition_assistance.application_approval.title")}}

    <VStack class="min-h-screen" ...attributes>
      <Section>
        <div class="flex-1 pb-32">
          <div class="max-w-7xl mx-auto">
            <VStack @collapsed={{true}}>
              {{! Application Header }}
              <Section>
                <:header>
                  {{yield to="header"}}
                </:header>
              </Section>

              {{! Approval Decision Assist Banner (admin only) — top of page }}
              {{#if this.features.isAdminApp}}
                <div class="mt-4 mb-2 px-4">
                  <ApprovalDecisionBanner
                    class="max-w-3xl"
                    @eligibilityData={{this.eligibilityData}}
                    @limitsData={{this.limitsData}}
                    @isLoading={{this.eligibilityLoading}}
                    @periodsOfEligibility={{this.periodsOfEligibilityData.value}}
                  />
                </div>
              {{/if}}

              {{! Admin-provided alerts (e.g., Course Cash verification banner) }}
              {{#if (has-block "alerts")}}
                {{yield to="alerts"}}
              {{/if}}

              <RecentCommentPreview @model={{@model}} @onViewMore={{this.openCommentsDrawer}} />

              <FinancialDetailsAdminSummary
                @application={{@model}}
                @limitsData={{this.limitsData}}
              />

              {{! Repayment Warning - Displayed at top of page }}
              {{#if this.showRepaymentWarning}}
                <Section>
                  <:body>
                    <div class="max-w-3xl">
                      <AlertBanner @intent="warning">
                        <div class="flex flex-col">
                          <p class="font-medium">
                            Employee has outstanding repayment obligations for tuition assistance
                            funds
                          </p>
                          <div class="flex gap-2 items-center">
                            <span class="font-medium shrink-0">
                              Application IDs:
                            </span>
                            <div class="flex flex-wrap gap-2">
                              {{#each this.repaymentApplicationIds as |app|}}
                                <LinkTo
                                  class="underline text-yellow-800 hover:text-yellow-900 font-medium text-sm"
                                  @route={{this.repaymentApplicationApprovalRoute}}
                                  @model={{app.applicationId}}
                                >
                                  {{app.applicationId}}
                                </LinkTo>
                              {{/each}}
                            </div>
                          </div>
                        </div>
                      </AlertBanner>
                    </div>
                  </:body>
                </Section>
              {{/if}}

              {{! AI Analysis Section -- Temporarily disabled }}
              {{!-- {{#if this.hasAiRecommendation}}
                <Section>
                  <:body>
                    <div
                      class="bg-purple-50/50 dark:bg-purple-900/20 rounded-lg border border-gray-200 overflow-hidden max-w-3xl"
                    >
                      {{! Header }}
                      <div
                        class="flex items-center justify-between px-6 py-4 border-b border-gray-200"
                      >
                        <div class="flex items-center gap-1.5">
                          <PhSparkle @size="16" />
                          <span class="text-base font-semibold text-gray-900">AI
                            Analysis</span>
                        </div>
                        <div class="flex items-center gap-2">
                          <Button
                            @appearance="minimal"
                            @intent="default"
                            @size="sm"
                            class="text-gray-500 hover:text-gray-700"
                            title="Approve"
                            data-test-quick-approve-button
                          >
                            <PhThumbsUp @size="20" />
                          </Button>
                          <Button
                            @appearance="minimal"
                            @intent="default"
                            @size="sm"
                            class="text-gray-500 hover:text-gray-700"
                            title="Reject"
                            data-test-quick-reject-button
                          >
                            <PhThumbsDown @size="20" />
                          </Button>
                        </div>
                      </div>
                      {{! Body }}
                      <div class="px-6 py-4 space-y-3">
                        <div class="flex gap-2">
                          <span
                            class="text-sm font-semibold text-gray-900"
                          >Recommendation:</span>
                          <span
                            class="text-sm font-normal text-gray-700"
                          >{{this.aiRecommendationStatus}}</span>
                        </div>
                        {{#if this.aiCalculationDetails}}
                          <p class="text-sm text-gray-700 leading-relaxed">
                            {{this.aiCalculationDetails}}
                          </p>
                        {{/if}}
                      </div>
                    </div>
                  </:body>
                </Section>
              {{/if}} --}}

              {{! Application Details Section }}
              <Section>
                <:header>
                  <div
                    class="flex items-center justify-between px-4 py-2.5 bg-gray-100/50 dark:bg-gray-800/50 border border-gray-200/80 dark:border-gray-700/80 rounded-[10px] max-w-3xl"
                  >
                    <h3 class="text-base font-semibold text-gray-900">Application Details</h3>
                    {{#if this.canEditApplicationDetails}}
                      <Button
                        @appearance="minimal"
                        @size="sm"
                        @icon={{if this.isEditingApplicationDetails PhX PhPencil}}
                        {{on "click" this.toggleApplicationDetailsEdit}}
                        data-test-edit-application-details-button
                        class="p-1.5!"
                      />
                    {{/if}}
                  </div>
                </:header>
                <:body>
                  {{#if this.isEditingApplicationDetails}}
                    {{! Edit Mode - Show form inputs }}
                    <div class="flex flex-col gap-4 p-4 max-w-3xl">
                      {{! Program Instance Fields }}
                      {{#each this.visibleProgramInstanceFieldConfigs as |fieldConfig|}}
                        {{#if (eq fieldConfig.name "SCHOOL_INSTITUTION_NAME")}}
                          {{! Special handling for school select }}
                          <SchoolSelect
                            @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
                            @selected={{this.selectedSchool}}
                            @onChange={{this.didSelectSchool}}
                            data-test={{fieldConfig.testId}}
                          />
                        {{else}}
                          <TuitionAssistanceProgramFormField
                            @name={{fieldConfig.name}}
                            @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
                            as |field|
                          >
                            {{#if field.isFieldOptionPicklist}}
                              <ReSelect
                                data-test={{fieldConfig.testId}}
                                @items={{field.fieldOptions}}
                                @selected={{this.getApplicationDetailValue fieldConfig.name}}
                                @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                                @size="sm"
                              />
                            {{else if (eq fieldConfig.type "currency")}}
                              <Input
                                data-test={{fieldConfig.testId}}
                                id={{field.inputId}}
                                @value={{this.getApplicationDetailValue fieldConfig.name}}
                                @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                                @size="sm"
                                {{inputmask
                                  alias="currency"
                                  prefix="$"
                                  unmaskAsNumber=false
                                  digits="2"
                                }}
                              />
                            {{else if (eq fieldConfig.type "date")}}
                              {{#let
                                (errorsForValibotField
                                  fieldConfig.name
                                  schemaErrors=this.courseDateFormValidationErrors
                                  hasSubmitted=true
                                )
                                as |fieldErrors|
                              }}
                                <div class="space-y-1">
                                  <div class="relative w-full">
                                    {{! @glint-expect-error: EmberFlatpickr type overrides}}
                                    <EmberFlatpickr
                                      @date={{or
                                        (this.getApplicationDetailValue fieldConfig.name)
                                        ""
                                      }}
                                      @onChange={{fn this.updateDateField fieldConfig.name}}
                                      @dateFormat="Y-m-d"
                                      @altInput={{true}}
                                      @altFormat="F j, Y"
                                      @disableMobile={{true}}
                                      data-test={{fieldConfig.testId}}
                                      placeholder="mm/dd/yyyy"
                                      id={{field.inputId}}
                                      class="tio-form-input
                                        {{if
                                          fieldErrors
                                          'border-red-500 dark:border-red-400'
                                          'border-gray-300'
                                        }}"
                                    />
                                    <div
                                      class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                                    >
                                      <PhCalendarBlank @size="20" />
                                    </div>
                                  </div>
                                  {{! Display validation errors }}
                                  {{#each fieldErrors as |error|}}
                                    <p class="text-sm text-red-600 dark:text-red-400">{{error}}</p>
                                  {{/each}}
                                </div>
                              {{/let}}
                            {{else}}
                              <Input
                                data-test={{fieldConfig.testId}}
                                id={{field.inputId}}
                                @value={{this.getApplicationDetailValue fieldConfig.name}}
                                @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                                @size="sm"
                              />
                            {{/if}}
                          </TuitionAssistanceProgramFormField>
                        {{/if}}
                      {{/each}}

                      {{! Application Fields }}
                      {{#each this.visibleApplicationFieldConfigs as |fieldConfig|}}
                        <TuitionAssistanceProgramFormField
                          @name={{fieldConfig.name}}
                          @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
                          as |field|
                        >
                          {{#if field.isFieldOptionPicklist}}
                            <ReSelect
                              data-test={{fieldConfig.testId}}
                              @items={{field.fieldOptions}}
                              @selected={{this.getApplicationDetailValue fieldConfig.name}}
                              @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                              @size="sm"
                            />
                          {{else if (eq fieldConfig.type "date")}}
                            {{#let
                              (errorsForValibotField
                                fieldConfig.name
                                schemaErrors=this.courseDateFormValidationErrors
                                hasSubmitted=true
                              )
                              as |fieldErrors|
                            }}
                              <div class="space-y-1">
                                <div class="relative w-full">
                                  {{! @glint-expect-error: EmberFlatpickr type overrides}}
                                  <EmberFlatpickr
                                    @date={{or
                                      (this.getApplicationDetailValue fieldConfig.name)
                                      ""
                                    }}
                                    @onChange={{fn this.updateDateField fieldConfig.name}}
                                    @dateFormat="Y-m-d"
                                    @altInput={{true}}
                                    @altFormat="F j, Y"
                                    @disableMobile={{true}}
                                    data-test={{fieldConfig.testId}}
                                    id={{field.inputId}}
                                    placeholder="mm/dd/yyyy"
                                    class="tio-form-input
                                      {{if
                                        fieldErrors
                                        'border-red-500 dark:border-red-400'
                                        'border-gray-300'
                                      }}"
                                  />
                                  <div
                                    class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                                  >
                                    <PhCalendarBlank @size="20" />
                                  </div>
                                </div>
                                {{! Display validation errors }}
                                {{#each fieldErrors as |error|}}
                                  <p class="text-sm text-red-600 dark:text-red-400">{{error}}</p>
                                {{/each}}
                              </div>
                            {{/let}}
                          {{else}}
                            <Input
                              data-test={{fieldConfig.testId}}
                              id={{field.inputId}}
                              @value={{this.getApplicationDetailValue fieldConfig.name}}
                              @onChange={{fn this.updateApplicationDetailField fieldConfig.name}}
                              @size="sm"
                            />
                          {{/if}}
                        </TuitionAssistanceProgramFormField>
                      {{/each}}

                      {{! Custom Fields - Edit Mode (Province-Based) }}
                      <TuitionAssistanceFormsCustomFieldsForProvince
                        @province="EMPLOYEE"
                        @customFields={{this.customEmployeeFields}}
                        @didUpdateFields={{this.didUpdateEmployeeCustomFields}}
                        @formValidationErrors={{this.customFieldValidationErrors}}
                        @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
                        @fieldContainerClass="p-2"
                      />

                      <TuitionAssistanceFormsCustomFieldsForProvince
                        @province="PROGRAM"
                        @customFields={{this.customProgramFields}}
                        @didUpdateFields={{this.didUpdateProgramCustomFields}}
                        @formValidationErrors={{this.customFieldValidationErrors}}
                        @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
                        @fieldContainerClass="p-2"
                      />

                      <TuitionAssistanceFormsCustomFieldsForProvince
                        @province="APPLICATION"
                        @customFields={{this.customApplicationFields}}
                        @didUpdateFields={{this.didUpdateApplicationCustomFields}}
                        @formValidationErrors={{this.customFieldValidationErrors}}
                        @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
                        @fieldContainerClass="p-2"
                      />

                      {{! Changelog textarea }}
                      <div>
                        <label class="flex font-semibold text-sm text-gray-700 mb-1">
                          Comment
                          <span class="text-red-600 dark:text-red-400 ml-1">*</span>
                        </label>
                        <Textarea
                          @value={{this.applicationDetailsChangelog}}
                          @onChange={{this.updateApplicationDetailsChangelog}}
                          @errors={{this.applicationDetailsChangelogValidationErrors}}
                          @size="sm"
                          data-test-application-details-changelog
                        />
                      </div>

                      {{! Save button }}
                      <div class="flex justify-end mt-2">
                        <Button
                          @intent="primary"
                          @appearance="outlined"
                          @size="sm"
                          @isRunning={{this.saveApplicationDetails.isRunning}}
                          {{on "click" this.saveApplicationDetails.perform}}
                          @isDisabled={{or
                            this.saveApplicationDetails.isRunning
                            (not this.isApplicationDetailsFormValid)
                          }}
                          data-test-save-application-details-button
                        >
                          {{if this.saveApplicationDetails.isRunning "Saving..." "Save Changes"}}
                        </Button>
                      </div>
                    </div>
                  {{else}}
                    {{! Read-only mode }}
                    <div class="divide-y divide-gray-200 max-w-3xl">
                      {{#each this.applicationDetailsRows as |row|}}
                        <div class="flex py-4 px-4">
                          <div
                            class="w-56 flex-shrink-0 text-sm font-medium text-gray-600"
                          >{{row.label}}</div>
                          {{#if row.linkRoute}}
                            <LinkTo
                              @route={{row.linkRoute}}
                              @model={{row.linkModel}}
                              class="text-sm font-medium text-gray-700 px-2 hover:underline cursor-pointer"
                            >
                              {{row.value}}
                            </LinkTo>
                          {{else}}
                            <ExpandableText
                              @text={{row.value}}
                              class="text-sm font-medium text-gray-700 px-2"
                            />
                          {{/if}}
                        </div>
                      {{/each}}

                      {{! Custom Application Detail Fields - Read-only (Province-Based) }}
                      {{! EMPLOYEE Province }}
                      {{#each this.customEmployeeFieldRows as |row|}}
                        <div class="flex py-4 px-4">
                          <div
                            class="w-56 flex-shrink-0 text-sm font-medium text-gray-600"
                          >{{row.label}}</div>
                          <ExpandableText
                            @text={{row.value}}
                            class="text-sm font-medium text-gray-700"
                          />
                        </div>
                      {{/each}}

                      {{! PROGRAM Province }}
                      {{#each this.customProgramFieldRows as |row|}}
                        <div class="flex py-4 px-4">
                          <div
                            class="w-56 flex-shrink-0 text-sm font-medium text-gray-600"
                          >{{row.label}}</div>
                          <ExpandableText
                            @text={{row.value}}
                            class="text-sm font-medium text-gray-700"
                          />
                        </div>
                      {{/each}}

                      {{! APPLICATION Province }}
                      {{#each this.customApplicationFieldRows as |row|}}
                        <div class="flex py-4 px-4">
                          <div
                            class="w-56 flex-shrink-0 text-sm font-medium text-gray-600"
                          >{{row.label}}</div>
                          <ExpandableText
                            @text={{row.value}}
                            class="text-sm font-medium text-gray-700"
                          />
                        </div>
                      {{/each}}
                    </div>
                  {{/if}}
                </:body>
              </Section>

              {{! Expenses Section }}
              <Section>
                <:header>
                  <div
                    class="flex items-center justify-between px-4 py-2.5 bg-gray-100/50 border border-gray-200/80 rounded-[10px] max-w-3xl"
                  >
                    <h3 class="text-base font-semibold text-gray-900">{{this.expenseType}}</h3>
                    <Popover
                      @isOpen={{this.isExpensesPopoverOpen}}
                      @onOpen={{this.openExpensesPopover}}
                      @onClose={{this.closeExpensesPopover}}
                      @placement="top-end"
                    >
                      <:trigger as |pop|>
                        <Button
                          @appearance="minimal"
                          @size="sm"
                          {{pop.register}}
                          {{on "click" this.openExpensesPopover}}
                          data-test-expenses-menu-trigger
                        >
                          <PhDotsThreeVertical @size="16" class="text-gray-500" />
                        </Button>
                      </:trigger>
                      <:body>
                        <Listbox @intent="primary" @onAction={{this.handleExpensesMenu}} as |l|>
                          <l.Item @key="addScholarship" data-test-add-scholarship-menu-item>
                            <span class="flex items-center gap-2">
                              <PhPlus @size="16" />
                              Add Scholarship
                            </span>
                          </l.Item>
                        </Listbox>
                      </:body>
                    </Popover>
                  </div>
                </:header>
                <:body>
                  <div class="divide-y divide-gray-200 max-w-3xl">
                    {{#each this.expenseRows as |row|}}
                      <div class="flex items-center justify-between py-4 px-4">
                        <div class="flex gap-4">
                          <div
                            class="w-96 text-sm font-medium text-gray-600 truncate"
                            title="{{row.label}}"
                          >{{row.label}}</div>
                          <div
                            class="w-56 text-sm font-medium
                              {{if
                                row.isTotal
                                'text-purple-600 dark:text-purple-400 font-semibold'
                                'text-gray-700'
                              }}"
                          >{{row.amount}}</div>
                        </div>
                        {{! Only show arrow for course and scholarship rows }}
                        {{#if (or (eq row.type "course") (eq row.type "scholarship"))}}
                          <Button
                            @appearance="minimal"
                            @size="sm"
                            {{on "click" (fn this.handleExpenseRowClick row)}}
                            class="p-1.5!"
                            data-test-open-expense-detail
                          >
                            <PhArrowRight @size="16" class="text-gray-400" />
                          </Button>
                        {{/if}}
                      </div>
                    {{/each}}
                  </div>
                </:body>
              </Section>
            </VStack>
          </div>
        </div>

        {{! New Footer with Action Buttons }}
        <ApprovalFooter
          @onDrawerOpen={{this.openDrawer}}
          @activeDrawer={{this.selectedDrawerType}}
          @approveButtonLoading={{false}}
          @application={{@model}}
          @isEditingApplication={{this.isEditingApplicationDetails}}
          @ai={{@ai}}
          @expenseType={{this.expenseType}}
          @onCourseCashOpen={{@onCourseCashOpen}}
        />
      </Section>
    </VStack>

    {{! Drawer Content }}
    <Drawer
      @isOpen={{this.isDrawerOpen}}
      @onClose={{this.closeDrawer}}
      @size="xl"
      @placement="right"
      @closeOnOutsideClick={{false}}
      @closeOnEscapeKey={{true}}
      @allowCloseButton={{true}}
      data-test-drawer
    >
      {{#if this.isAiAnalysis}}
        <AiAnalysisDrawer @application={{@model}} @recommendations={{@model.aiRecommendations}} />
      {{else if this.isComments}}
        <CommentsDrawer
          @model={{@model}}
          {{! @glint-expect-error: sessionContext not typed in common}}
          @authoredByPerson={{this.sessionContext.user.person}}
          {{! @glint-expect-error: sessionContext not typed in common}}
          @authoredByUser={{this.sessionContext.user}}
          @initialTab={{this.commentsInitialTab}}
        />
      {{else if this.isCourseDetails}}
        <CoursesDrawer
          @courses={{@model.activeCourses}}
          @selectedCourseIndex={{this.selectedCourseIndex}}
          @onCourseTabChange={{this.courseTabDidChange}}
          @courseExpensesFor={{this.courseExpensesFor}}
          @courseDetailsFor={{this.courseDetailsFor}}
          @courseCustomFieldsFor={{this.courseCustomFieldsFor}}
          @getTabLabel={{this.getTabLabel}}
          @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
          @canEditCourse={{this.canEditCourse}}
          {{! Expense editing props }}
          @isEditingExpenses={{this.isEditingCourseExpenses}}
          @onToggleExpenseEdit={{this.toggleCourseExpenseEdit}}
          @onSaveExpenses={{this.saveCourseExpenses.perform}}
          @onUpdateExpense={{this.updateCourseExpenseField}}
          @getCourseExpenseValue={{this.getCourseExpenseValue}}
          @expenseChangelog={{this.expenseChangelog}}
          @onExpenseChangelogUpdate={{this.updateExpenseChangelog}}
          @expenseChangelogErrors={{this.expenseChangelogValidationErrors}}
          @isExpenseFormValid={{this.isExpenseFormValid}}
          @isExpensesSaving={{this.saveCourseExpenses.isRunning}}
          {{! Detail editing props }}
          @isEditingDetails={{this.isEditingCourseDetails}}
          @onToggleDetailEdit={{this.toggleCourseDetailEdit}}
          @onSaveDetails={{this.saveCourseDetails.perform}}
          @onUpdateDetail={{this.updateCourseDetailField}}
          @onDidUpdateCourseCustomFields={{this.didUpdateCourseCustomFields}}
          @getCourseDetailValue={{this.getCourseDetailValue}}
          @courseCustomFieldValidationErrors={{this.courseCustomFieldValidationErrors}}
          @detailChangelog={{this.detailChangelog}}
          @onDetailChangelogUpdate={{this.updateDetailChangelog}}
          @detailChangelogErrors={{this.detailChangelogValidationErrors}}
          @isDetailFormValid={{this.isDetailFormValid}}
          @isDetailsSaving={{this.saveCourseDetails.isRunning}}
          @fieldsCopy={{this.courseDetailFieldsCopy}}
          @formValidationErrors={{this.courseDetailFormValidationErrors}}
        />
      {{else if this.isEmployerInfo}}
        <PartnerInfoDrawer
          @model={{@model}}
          @approverInstructionsItems={{this.approverInstructionsItems}}
          @employeeFaqsItems={{this.employeeFaqsItems}}
          @isAdminApp={{this.features.isAdminApp}}
        />
      {{else if this.isFinancialDetails}}
        <FinancialDetailsDrawer
          @application={{@model}}
          @programInstance={{@model.tasProgramInstance}}
          @editableApplicationTotal={{this.editableApplicationTotal}}
          @editableCourseCashTotal={{this.editableCourseCashTotal}}
          @isReadonlyView={{false}}
          @onUpdateApprovedTotal={{this.updateApprovedTotal}}
          @onSaveClaimsFinancials={{this.saveUpdatedClaimsFinancialsWithApprovedTotal}}
          @onUpdateClaimsFinancials={{this.didUpdateClaimsFinancials}}
          @onSetApplicationTotal={{this.setApplicationTotal}}
          @onSetCourseCashTotal={{this.setCourseCashTotal}}
          @onInvoiceDateChange={{this.updateInvoiceDate}}
        />
      {{else if this.isAttachments}}
        <AttachmentsDrawer
          @model={{@model}}
          @allAttachments={{this.allAttachments}}
          @classificationOptions={{classificationOptions}}
          @defaultClassification={{this.selectedClassification}}
          @programTemplate={{@model.tasProgramInstance.tasProgramTemplate}}
          @onUploadComplete={{this.hideUploadSection}}
          as |sections|
        >
          {{! Render upload section }}
          <sections.UploadSection
            @canUpload={{sections.uploadProps.canUpload}}
            @showUploadSection={{sections.uploadProps.showUploadSection}}
            @toggleUploadSection={{sections.uploadProps.toggleUploadSection}}
            @selectedClassification={{sections.uploadProps.selectedClassification}}
            @classificationDidChange={{sections.uploadProps.classificationDidChange}}
            @uploadFile={{sections.uploadProps.uploadFile}}
            @classificationOptions={{sections.uploadProps.classificationOptions}}
          />

          {{! Conditionally render analysis view for tio-admin, table view for others }}
          {{#if (eq this.consumingApp "tio-admin")}}
            <AttachmentsAnalysisView
              @attachments={{sections.attachments}}
              @assetSummaries={{sections.assetSummaries}}
              @deleteAttachment={{sections.deleteAttachment}}
              @isDeleted={{sections.isDeleted}}
            />
          {{else}}
            <sections.TableView
              @sortedAttachments={{sections.tableProps.sortedAttachments}}
              @getFileIcon={{sections.tableProps.getFileIcon}}
              @getClassificationLabel={{sections.tableProps.getClassificationLabel}}
              @isDeleted={{sections.tableProps.isDeleted}}
              @deleteAttachment={{sections.tableProps.deleteAttachment}}
              @disabled={{sections.tableProps.disabled}}
              @showUploadSection={{sections.tableProps.showUploadSection}}
            />
          {{/if}}
        </AttachmentsDrawer>
      {{else if this.isTimeline}}
        <TimelineDrawer @formattedHistory={{this.formattedHistory}} />
      {{else if this.isReassignApprover}}
        <ApprovalQueueStatusDrawer @application={{@model}} @consumingApp={{this.consumingApp}} />
      {{/if}}
    </Drawer>

    {{! Scholarship Drawer }}
    <AddScholarshipsDrawer
      @isOpen={{this.isScholarshipDrawerOpen}}
      @onClose={{this.closeScholarshipDrawer}}
      @application={{@model}}
    />
  </template>
}
